/* =====================================================================
   BoldZee Free AI Visibility Score
   assets/bz-ai-score.js   (vanilla JS, no build step, no libraries)

   Modules, top to bottom:
     1. COPY        every visible string (edit text here)
     2. CONFIG      merges window.BZ_AI_SCORE over defaults
     3. UTIL        helpers, storage (try/catch), icons
     4. ANALYTICS   window.dataLayer events
     5. MOCK        fake backend with fixtures (CONFIG.mock = true)
     6. API         client for the Cloudflare Worker contract
     7. TURNSTILE   Cloudflare bot check
     8. OFFERS      score band logic and product cards
     9. RESULTS     results renderer (renders ONLY from the RESULT object)
    10. SHARE       copy link, badge download, re-check reminder
    11. POLLING     check status polling and the live progress card
    12. PAGE        /pages/ai-score steps 1 to 3, states, FAQ, final CTA
    13. POPUP       home page pop-up and corner badge
    14. INIT
   ===================================================================== */
(function () {
  'use strict';
  if (window.__bzsLoaded) return;
  window.__bzsLoaded = true;

  /* ===================================================================
     1. COPY (no em dashes anywhere; use periods, commas, colons, semicolons)
     Tokens in {curly braces} are filled in at runtime.
     =================================================================== */
  var COPY = {
    popup: {
      eyebrow: 'Free AI Visibility Score',
      title: 'Does ChatGPT recommend your business?',
      hook: 'A 2026 study of about 350,000 locations found only about 1.2% appeared in ChatGPT’s local recommendations. See where you stand in about 60 seconds.',
      source: 'Source: {source}',
      label: 'Your email',
      placeholder: 'you@yourbusiness.com',
      button: 'Get my free score',
      sending: 'Sending...',
      free: '100% free. No card needed. No sales call.',
      close: 'Close',
      badge: 'Free AI Visibility Score'
    },
    consent: {
      text: 'Yes, send me my score and BoldZee tips. Unsubscribe anytime.',
      privacy: 'Privacy policy'
    },
    errors: {
      email: 'Please enter a valid email, like name@yourbusiness.com.',
      consent: 'Please tick the box so we can email your score.',
      url: 'Enter a full website address, like yourbusiness.com (no spaces).',
      name: 'Enter your business name.',
      city: 'Enter the main city you serve.',
      category: 'Tell us what you do in one or two words, like plumber.',
      server: 'Something went wrong on our side. Please try again in a minute.',
      bot: 'We could not confirm you are a person. Please try again.',
      token: 'Please confirm your email again so we know where to send your score.',
      tryAgain: 'Try again'
    },
    intro: {
      eyebrow: 'Free tool for the AI era',
      title: 'Free AI Visibility Score',
      sub: 'Find out if ChatGPT, Gemini and Perplexity recommend your business when customers ask for what you do in your city.',
      stats: [
        ['Price', 'Free'],
        ['Time', 'About 60 seconds'],
        ['We check', '3 AI tools + Google'],
        ['Card needed', 'No']
      ],
      tracker: ['Email', 'Business details', 'Your score'],
      trackerLabel: 'Progress',
      stepDone: 'done',
      stepActive: 'current step',
      stepPending: 'not started'
    },
    step1: {
      title: '1. Where should we send your score?',
      label: 'Your email',
      button: 'Continue',
      done: 'We will email your score to {email}',
      change: 'Change',
      under: ['100% free', 'No card needed', 'No sales call']
    },
    step2: {
      title: '2. Tell us about your business',
      sub: 'We ask AI tools the same questions your customers ask.',
      website: 'Website',
      websitePh: 'yourbusiness.com',
      name: 'Business name',
      namePh: 'As customers see it',
      city: 'City',
      cityPh: 'Maple Ridge, BC',
      category: 'Business category',
      categoryPh: 'Example: plumber, dentist, roofing company',
      suggestTitle: 'We found these on Google. Is one of them you?',
      suggestUse: 'Use this',
      suggestFoot: 'Not listed? Just fill in the fields below.',
      whyLead: 'Why we ask for your city:',
      why: 'AI tools recommend businesses by name and city, not only by website.',
      button: 'Check my AI visibility',
      buttonAgain: 'Check again',
      sending: 'Starting your check...',
      under: ['100% free', 'No card needed', 'One free check per business per day'],
      sideTitle: 'What we check',
      checks: [
        ['chat', 'ChatGPT', 'Does it name you when customers ask for your service in your city?'],
        ['sparkle', 'Gemini', 'Are you recommended, and are your details correct?'],
        ['search', 'Perplexity', 'Which sources does it trust, and are you one of them?'],
        ['pin', 'Google Business Profile', 'Categories, reviews and activity that AI tools read.'],
        ['globe', 'Website signals', 'Clear city, services and local business markup.']
      ]
    },
    step3: {
      title: '3. Checking your AI visibility',
      sub: 'Checking {name} in {city}',
      doneTitle: 'Check complete. Your score is below.',
      doneSub: 'Scroll down to see your results.',
      progress: 'Progress',
      progressLabel: 'Check progress',
      steps: {
        chatgpt: 'Asking ChatGPT...',
        gemini: 'Asking Gemini...',
        perplexity: 'Asking Perplexity...',
        gbp: 'Checking your Google profile...',
        scoring: 'Calculating your score...'
      },
      stateDone: 'Done',
      stateActive: 'Working',
      factLabel: 'Did you know?',
      facts: [
        'AI tools look for the same business name, address and phone number across the web. Small differences can make them skip you.',
        'When people ask AI for a local business, it usually names only a few options. Being one of them matters.',
        'Recent Google reviews that mention your services and city help AI tools understand what you do and where.',
        'Clear service pages with your city in the text make it easier for AI tools to recommend you by name.',
        'Your free score is a quick snapshot. The full audit checks many more questions and every competitor AI names.'
      ],
      note: 'This usually takes 30 to 60 seconds. You can close this page; we will email your score.'
    },
    states: {
      deferredTitle: 'We couldn’t check right now. We’ll email your score.',
      deferredText: 'The AI tools are slow to answer right now. Your check is saved and keeps running. We will email your score to {email} as soon as it is ready.',
      deferredTextNoEmail: 'The AI tools are slow to answer right now. Your check is saved and keeps running. We will email your score as soon as it is ready.',
      savedTitle: 'Your saved details',
      home: 'Back to BoldZee home',
      store: 'Browse the Service Store',
      limitTitle: 'One free check per business per day',
      limitText: 'We already checked this business today and sent the score to {email}.',
      limitRetry: 'You can run a new free check after {date}.',
      viewToday: 'View today’s score',
      loading: 'Loading...',
      errorTitle: 'Something went wrong on our side.',
      errorText: 'Please try again in a minute.'
    },
    summaryLabels: { website: 'Website', name: 'Business', city: 'City', category: 'Category', email: 'Email' },
    results: {
      emailed: 'We emailed you a copy of this score at {email}.',
      eyebrow: 'Your AI Visibility Score',
      eyebrowShared: 'Shared AI Visibility Score',
      checked: 'Checked {date}',
      of100: 'OUT OF 100',
      gradeLabel: 'Grade',
      ringLabel: 'AI Visibility Score {score} out of 100, grade {grade}',
      tags: { low: 'Low visibility', mid: 'Medium visibility', high: 'Strong visibility' },
      stats: { namedIn: 'Named in', namedInValue: '{n} of {t} answers', tools: 'AI tools checked', competitors: 'Competitors found', fixes: 'Quick fixes found' },
      breakdownTitle: 'Your breakdown',
      breakdownSub: 'What each AI tool and Google signal says about your business.',
      status: { pass: 'Pass', warning: 'Warning', fail: 'Fail' },
      compTitle: 'Who AI recommends instead',
      compSub: 'Businesses AI tools named when customers asked for a {category} in {city}.',
      compNone: 'AI tools did not name a clear competitor in these answers.',
      unlock: '{n} more businesses · Unlock in the full audit',
      problemsTitle: 'Top 3 quick problems',
      problemsSub: 'Fix these first. Each one makes it harder for AI tools to recommend you.',
      severity: { high: 'High', medium: 'Medium', low: 'Low' }
    },
    cta: {
      tagLow: 'Best next step for your score',
      tagMid: 'Fix the gaps that cost you customers',
      tagHigh: 'Find the gaps before competitors do',
      title: 'AI Visibility Audit',
      line: 'See exactly where AI tools skip you, who they pick instead, and how to fix it.',
      includedLabel: 'What’s included',
      included: [
        'Results from ChatGPT, Gemini, Perplexity and Google AI for 20 real customer questions',
        'The full list of businesses AI recommends instead, and why',
        'Google Business Profile and website fixes, in priority order',
        'A clear action plan your team can follow, or we can do it for you'
      ],
      delivery: 'Delivery',
      payment: 'Payment',
      paymentValue: 'One-time',
      total: 'Total',
      price: '$149',
      button: 'Get the full AI Visibility Audit, $149',
      fine: 'One-time price. No subscription, no retainer.',
      spots: 'Limited audit spots this week',
      trust: ['Transparent pricing', 'Defined deliverables', 'Vancouver team']
    },
    offers: {
      titleLow: 'Other services that fit your score',
      titleMid: 'Other services that fit your score',
      titleHigh: 'Stay on top',
      sub: 'Productized services with fixed scope and clear prices.',
      recommended: 'Recommended for your score',
      button: 'View service',
      swipe: 'Swipe to see more',
      localSeo: { cat: 'Local SEO', title: 'Local SEO Services', price: '$1,149', unit: 'setup', desc: 'Get found in Google Maps and AI answers in your city. One location per order.', del: 'Then $1,299/month · 3-month first term' },
      seoAudit: { cat: 'Audit', title: 'SEO Audit', price: '$149', unit: '', desc: 'A full review of your website’s search health, with a clear fix list in priority order.', del: 'One-time price · Defined deliverables' },
      growthCare: { cat: 'Growth', title: 'Growth Care Plan', price: '{growthCarePrice}', unit: '/month', desc: 'Specialists review your growth every month and recommend fixes. You approve, our team implements.', del: 'Cancel anytime · No retainer' }
    },
    share: {
      title: 'Share your score',
      badgeLabel: 'AI Visibility Score',
      badgeBy: 'Checked by BoldZee',
      badgeAria: 'Badge preview: grade {grade}, AI Visibility Score {score} out of 100, checked by BoldZee',
      copy: 'Copy link',
      copied: 'Link copied',
      download: 'Download badge',
      fileName: 'boldzee-ai-visibility-score.png'
    },
    recheck: {
      title: 'Track your progress',
      text: 'AI answers change often. We will email you in 30 days with a one-click link to check again and compare.',
      button: 'Re-check in 30 days',
      done: 'Reminder set for {date}',
      fine: 'Free. One email, no spam.'
    },
    shared: {
      text: 'Someone shared their AI Visibility Score with you.',
      small: 'Free check in about 60 seconds. No card needed.',
      button: 'Check your own business for free'
    },
    faq: {
      eyebrow: 'Questions',
      title: 'About the free score',
      items: [
        ['tag', 'Is the AI Visibility Score really free?', 'Yes. It is 100% free. No card, no subscription and no sales call. You see your score on this page and we email you a copy.'],
        ['search', 'What do you check?', 'We ask ChatGPT, Gemini and Perplexity the questions your customers ask, using your business name, city and category. We also review your Google Business Profile and key website signals.'],
        ['mail', 'What do you do with my email?', 'We send your score and, if you agree, short BoldZee tips. You can unsubscribe anytime. We never sell your details. See our {privacy} for more.'],
        ['refresh', 'How often can I check?', 'One free check per business per day. AI answers change often, so we suggest checking again every 30 days.'],
        ['layers', 'How is the free score different from the $149 audit?', 'The free score is a quick snapshot of 15 AI answers. The AI Visibility Audit checks 20 real customer questions, names every business AI recommends instead, and gives you a clear fix plan in priority order.']
      ],
      privacyWord: 'privacy policy'
    },
    final: {
      eyebrow: 'Next step',
      title: 'Become the business AI recommends.',
      text: 'Get the full AI Visibility Audit with defined deliverables, a fixed price and no retainer.',
      primary: 'Get the full AI Visibility Audit, $149',
      secondary: 'Browse the Service Store',
      trust: ['Productized services', 'Transparent pricing', 'No retainers']
    },
    footer: {
      note: 'The free score is a snapshot of AI answers on the day you check. AI answers change often.',
      privacy: 'Privacy policy',
      store: 'Service Store'
    },
    review: {
      eyebrow: 'Design review',
      title: 'Every state, for review'
    },
    noscript: 'Please turn on JavaScript to use the free AI Visibility Score.'
  };

  /* ===================================================================
     2. CONFIG
     =================================================================== */
  var DEFAULTS = {
    apiBase: 'https://score.boldzee.com',
    mock: true,
    turnstileSiteKey: '[SITE KEY]',
    pollTimeoutMs: 90000,
    pollDefaultMs: 2000,
    requestTimeoutMs: 20000,
    bands: { lowMax: 59, midMax: 79 },
    pageUrl: '/pages/ai-score',
    homeUrl: '/',
    storeUrl: '/collections/all',
    privacyUrl: '/policies/privacy-policy',
    studySource: '[STUDY NAME, 2026]',
    auditDelivery: '[CONFIRM: 3 business days]',
    growthCarePrice: '[PRICE]',
    products: {
      audit: '/products/[HANDLE]',
      localSeo: '/products/[HANDLE]',
      seoAudit: '/products/[HANDLE]',
      growthCare: '/products/[HANDLE]'
    },
    popup: { delayMs: 8000, scrollRatio: 0.4, snoozeDays: 7 },
    mockDurationMs: 15000,
    copy: null /* optional partial COPY override object */
  };

  function deepMerge(base, over) {
    var out = {};
    Object.keys(base).forEach(function (k) { out[k] = base[k]; });
    if (!over || typeof over !== 'object') return out;
    Object.keys(over).forEach(function (k) {
      var b = base[k], o = over[k];
      if (o === undefined) return;
      if (b && o && typeof b === 'object' && typeof o === 'object' && !Array.isArray(b) && !Array.isArray(o)) out[k] = deepMerge(b, o);
      else out[k] = o;
    });
    return out;
  }
  var CFG = deepMerge(DEFAULTS, window.BZ_AI_SCORE || {});
  if (CFG.copy) COPY = deepMerge(COPY, CFG.copy);

  /* ===================================================================
     3. UTIL
     =================================================================== */
  var KEYS = {
    popupDone: 'bzs_popup_done',
    popupClosedAt: 'bzs_popup_closed_at',
    pendingLead: 'bzs_pending_lead',
    utm: 'bzs_utm',
    lead: 'bzs_lead',
    mock: 'bzs_mock'
  };

  var store = {
    get: function (area, key) {
      try { var v = window[area].getItem(key); return v ? JSON.parse(v) : null; } catch (e) { return null; }
    },
    set: function (area, key, val) {
      try { window[area].setItem(key, JSON.stringify(val)); } catch (e) { /* storage blocked: fine */ }
    },
    del: function (area, key) {
      try { window[area].removeItem(key); } catch (e) { /* ignore */ }
    }
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* Fill {tokens}. Values are escaped unless raw === true. */
  function fmt(str, vars, raw) {
    return String(str).replace(/\{(\w+)\}/g, function (m, k) {
      if (!vars || vars[k] === undefined) return m;
      return raw ? String(vars[k]) : esc(vars[k]);
    });
  }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function params() { try { return new URLSearchParams(window.location.search); } catch (e) { return { get: function () { return null; } }; } }
  function reducedMotion() { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } }
  function scrollToEl(el) {
    if (!el) return;
    try { el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) { el.scrollIntoView(); }
  }
  function focusEl(el) {
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(v || '').trim()); }
  function cleanUrl(v) {
    return String(v || '').trim().replace(/^https?:\/\//i, '').replace(/^\/+/, '').replace(/\/+$/, '');
  }
  function isUrl(v) {
    var s = cleanUrl(v);
    if (!s || /\s/.test(s)) return false;
    return /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i.test(s);
  }
  function domainOf(v) { return cleanUrl(v).split('/')[0].toLowerCase().replace(/^www\./, ''); }
  function fmtDate(d) {
    var dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt)) return '';
    try { return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return dt.toDateString(); }
  }
  function fmtDateLong(d) {
    var dt = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T12:00:00') : new Date(d);
    if (isNaN(dt)) return String(d);
    try { return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch (e) { return dt.toDateString(); }
  }
  function fmtDateTime(d) {
    var dt = new Date(d);
    if (isNaN(dt)) return '';
    try { return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return dt.toString(); }
  }
  function maskEmail(e) {
    var p = String(e || '').split('@');
    if (p.length !== 2) return e;
    return p[0].charAt(0) + '***@' + p[1];
  }
  function utm() {
    var p = params(), saved = store.get('sessionStorage', KEYS.utm) || { source: '', medium: '', campaign: '' };
    var src = p.get('utm_source'), med = p.get('utm_medium'), cam = p.get('utm_campaign');
    if (src || med || cam) {
      saved = { source: src || '', medium: med || '', campaign: cam || '' };
      store.set('sessionStorage', KEYS.utm, saved);
    }
    return saved;
  }
  function productUrl(key) { return (CFG.products && CFG.products[key]) || '#'; }

  var I = {
    check: '<path d="M20 6 9 17l-5-5"/>',
    x: '<path d="M18 6 6 18M6 6l12 12"/>',
    chat: '<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/>',
    sparkle: '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="M19 15v4M17 17h4"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
    pin: '<path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>',
    globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>',
    alert: '<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5h.01"/>',
    warn: '<path d="M12 4 2.5 20h19z"/><path d="M12 10v4M12 17h.01"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
    lock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>',
    mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
    tag: '<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/>',
    refresh: '<path d="M20 11a8 8 0 0 0-14.9-3M4 4v4h4M4 13a8 8 0 0 0 14.9 3M20 20v-4h-4"/>',
    layers: '<path d="m12 3 9 5-9 5-9-5z"/><path d="m3 13 9 5 9-5"/>',
    chevron: '<path d="m9 6 6 6-6 6"/>',
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    link: '<path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>',
    download: '<path d="M12 4v11M7 10l5 5 5-5M5 20h14"/>',
    bell: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>',
    shield: '<path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z"/><path d="m9 12 2 2 4-4"/>',
    bulb: '<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z"/>',
    bag: '<path d="M5 8h14l-1 13H6z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
    home: '<path d="M3 11 12 4l9 7"/><path d="M5 10v10h14V10"/>',
    star: '<path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z"/>',
    eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>'
  };
  function icon(name, cls) {
    return '<svg' + (cls ? ' class="' + cls + '"' : '') + ' viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + (I[name] || '') + '</svg>';
  }
  var SOURCE_ICON = { chatgpt: 'chat', gemini: 'sparkle', perplexity: 'search', gbp: 'pin', website: 'globe' };
  var STATUS_ICON = { pass: 'check', warning: 'warn', fail: 'x' };

  /* ===================================================================
     4. ANALYTICS
     =================================================================== */
  var Analytics = {
    push: function (event, data) {
      try {
        window.dataLayer = window.dataLayer || [];
        var o = { event: event };
        if (data) Object.keys(data).forEach(function (k) { o[k] = data[k]; });
        window.dataLayer.push(o);
      } catch (e) { /* never break the tool for analytics */ }
    }
  };

  /* ===================================================================
     5. MOCK ADAPTER
     Answers every endpoint with fixtures, so the page works with no backend.
     Scenarios: ?bzmock=low|mid|high|deferred|limit|error|badbot
     A website containing "timeout" gives deferred; "limit" gives limit_reached.
     =================================================================== */
  var FIXTURES = {
    low: {
      shareId: '7KQ2M', score: 34, grade: 'F',
      headline: 'AI tools rarely recommend your business.',
      text: 'When we asked AI tools for a {category} in {city}, your business was named in 1 of 15 answers. Other businesses were named far more often. Your Google Business Profile and website are missing signals AI tools use to decide who to recommend.',
      stats: { namedIn: 1, totalAnswers: 15, toolsChecked: '3 + Google', competitorsFound: 5, quickFixes: 7 },
      sources: [
        ['chatgpt', 'ChatGPT', 'fail', '0 of 5 answers', 'Named other businesses in {city}, not you.'],
        ['gemini', 'Gemini', 'warning', '1 of 5 answers', 'Named you once, with an old phone number.'],
        ['perplexity', 'Perplexity', 'fail', '0 of 5 answers', 'Cites directory sites that do not list your business.'],
        ['gbp', 'Google Business Profile', 'warning', '3 of 6 checks passed', 'Only one category, and few recent reviews mention your services.'],
        ['website', 'Website signals', 'fail', '1 of 4 signals found', 'No local business markup, and your city is not on your service pages.']
      ],
      competitors: [['Ridgeline Plumbing Co.', 'Named by ChatGPT and Perplexity'], ['Fraser Valley Pipe Pros', 'Named by Gemini in 3 of 5 answers']],
      locked: 3,
      problems: [
        ['Your name, address and phone do not match across the web', 'Your phone number on Yelp is different from your Google profile. AI tools may skip businesses with mixed details.', 'high'],
        ['Your Google Business Profile has only one category', 'Add the services you offer as extra categories, so AI tools match you to more customer questions.', 'high'],
        ['Your website does not say which city you serve', 'Add {city} to your home page and to each service page, so AI tools connect your services to your city.', 'medium']
      ]
    },
    mid: {
      shareId: '8MID4', score: 68, grade: 'C',
      headline: 'AI tools sometimes recommend your business.',
      text: 'You were named in 7 of 15 answers. ChatGPT knows you, but Gemini and Perplexity often name other businesses first. A few fixes can move you into more answers.',
      stats: { namedIn: 7, totalAnswers: 15, toolsChecked: '3 + Google', competitorsFound: 4, quickFixes: 4 },
      sources: [
        ['chatgpt', 'ChatGPT', 'pass', '3 of 5 answers', 'Named you for most questions about your service in {city}.'],
        ['gemini', 'Gemini', 'warning', '2 of 5 answers', 'Named you, but listed your old opening hours.'],
        ['perplexity', 'Perplexity', 'warning', '2 of 5 answers', 'Trusts two directories where your details are out of date.'],
        ['gbp', 'Google Business Profile', 'pass', '5 of 6 checks passed', 'Good reviews and categories. No posts in the last 90 days.'],
        ['website', 'Website signals', 'warning', '2 of 4 signals found', 'Your city is clear, but local business markup is missing.']
      ],
      competitors: [['Ridgeline Plumbing Co.', 'Named by Gemini and Perplexity'], ['Haney Home Services', 'Named by Perplexity in 2 of 5 answers']],
      locked: 2,
      problems: [
        ['Your details are out of date on two directories', 'Perplexity reads these sites first. Update your hours and phone number so AI tools trust your listing.', 'medium'],
        ['Your website is missing local business markup', 'Add local business markup with your name, address, phone and service area, so AI tools read your details correctly.', 'medium'],
        ['Your Google profile has no recent posts', 'Post an update every few weeks. Fresh activity helps AI tools see that your business is active.', 'low']
      ]
    },
    high: {
      shareId: '9HIG7', score: 86, grade: 'B',
      headline: 'AI tools often recommend your business.',
      text: 'You were named in 12 of 15 answers. You are one of the first businesses AI tools suggest in {city}. Keep your details fresh so you stay there.',
      stats: { namedIn: 12, totalAnswers: 15, toolsChecked: '3 + Google', competitorsFound: 3, quickFixes: 2 },
      sources: [
        ['chatgpt', 'ChatGPT', 'pass', '5 of 5 answers', 'Named you in every answer, often first.'],
        ['gemini', 'Gemini', 'pass', '4 of 5 answers', 'Recommends you with correct details.'],
        ['perplexity', 'Perplexity', 'warning', '3 of 5 answers', 'One source it trusts still shows your old address.'],
        ['gbp', 'Google Business Profile', 'pass', '6 of 6 checks passed', 'Complete profile with steady recent reviews.'],
        ['website', 'Website signals', 'pass', '4 of 4 signals found', 'Clear city, services and local business markup.']
      ],
      competitors: [['Ridgeline Plumbing Co.', 'Named by Perplexity in 2 of 5 answers']],
      locked: 2,
      problems: [
        ['One directory still shows your old address', 'Perplexity cites it. Update it so every AI tool sees the same details.', 'low'],
        ['Few recent reviews mention your city', 'Ask happy customers to mention the service and the city in their review.', 'low'],
        ['Your FAQ page does not answer price questions', 'Customers ask AI about cost. A short pricing FAQ gives AI tools a clear answer to quote.', 'low']
      ]
    }
  };
  var FAKE_LOCKED = ['Westside Plumbing Co.', 'Coastline Heating Ltd.', 'Valley Pro Services', 'Summit Home Services', 'Lakeview Repair Group'];
  var DEFAULT_BIZ = { name: 'Northline Plumbing & Heating', website: 'northlineplumbing.ca', city: 'Maple Ridge, BC', category: 'Plumber' };

  function buildResult(band, biz, checkId) {
    var f = FIXTURES[band] || FIXTURES.low;
    var b = biz || DEFAULT_BIZ;
    var vars = { city: b.city, category: String(b.category || '').toLowerCase() };
    return {
      checkId: checkId || 'ck_789',
      shareId: f.shareId,
      checkedAt: new Date().toISOString(),
      business: { name: b.name, website: domainOf(b.website), city: b.city, category: b.category },
      score: f.score,
      grade: f.grade,
      summary: { headline: f.headline, text: fmt(f.text, vars, true) },
      stats: JSON.parse(JSON.stringify(f.stats)),
      sources: f.sources.map(function (s) { return { id: s[0], label: s[1], status: s[2], metric: s[3], note: fmt(s[4], vars, true) }; }),
      competitors: { visible: f.competitors.map(function (c) { return { name: c[0], reason: c[1] }; }), lockedCount: f.locked },
      problems: f.problems.map(function (p) { return { title: p[0], detail: fmt(p[1], vars, true), severity: p[2] }; }),
      share: { url: 'https://boldzee.com/pages/ai-score?r=' + f.shareId, badgeUrl: null }
    };
  }

  var Mock = {
    scenario: function () {
      var q = params().get('bzmock');
      if (q) { store.set('sessionStorage', KEYS.mock, q); return q; }
      return store.get('sessionStorage', KEYS.mock) || 'low';
    },
    db: function () { return store.get('sessionStorage', 'bzs_mock_db') || { leads: {}, checks: {}, attempts: {} }; },
    save: function (db) { store.set('sessionStorage', 'bzs_mock_db', db); },
    reply: function (status, data, ms) {
      return wait(ms == null ? 450 : ms).then(function () { return { ok: status >= 200 && status < 300, status: status, data: data }; });
    },
    err: function (status, code, message, extra) {
      var d = { error: { code: code, message: message || code } };
      if (extra) Object.keys(extra).forEach(function (k) { d[k] = extra[k]; });
      return Mock.reply(status, d);
    },
    handle: function (method, path, body) {
      var db = Mock.db(), sc = Mock.scenario(), m;
      var url = new URL(path, 'https://mock.local');
      var p = url.pathname;

      if (method === 'POST' && p === '/v1/leads') {
        if (!body || !isEmail(body.email)) return Mock.err(400, 'invalid_email', COPY.errors.email);
        if (!body.consent || body.consent.given !== true) return Mock.err(422, 'consent_required', COPY.errors.consent);
        var lt = 'lt_' + Math.random().toString(36).slice(2, 10);
        db.leads[lt] = body.email.trim();
        Mock.save(db);
        return Mock.reply(201, { leadId: 'ld_' + Math.random().toString(36).slice(2, 7), leadToken: lt, email: body.email.trim() }, 600);
      }
      if (method === 'GET' && p === '/v1/leads/me') {
        var tok = url.searchParams.get('lt');
        var email = db.leads[tok] || (tok === 'lt_demo' ? 'dan@northlineplumbing.ca' : null);
        if (!email) return Mock.err(404, 'invalid_token', 'Unknown token');
        return Mock.reply(200, { leadId: 'ld_123', email: email }, 350);
      }
      if (method === 'GET' && p === '/v1/suggest') {
        var d = domainOf(url.searchParams.get('url') || '');
        if (!d || /none|nomatch/.test(d)) return Mock.reply(200, { suggestions: [] }, 500);
        var stem = d.split('.')[0].replace(/[-_]+/g, ' ');
        var nice = stem.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        var list = /northline/.test(d)
          ? [
            { placeId: 'ChIJmock01', name: 'Northline Plumbing & Heating', address: '22000 Lougheed Hwy', city: 'Maple Ridge, BC', category: 'Plumber' },
            { placeId: 'ChIJmock02', name: 'Northline Plumbing (Pitt Meadows)', address: '19150 Lougheed Hwy', city: 'Pitt Meadows, BC', category: 'Plumber' }
          ]
          : [
            { placeId: 'ChIJmock11', name: nice, address: '11900 Haney Pl', city: 'Maple Ridge, BC', category: 'Contractor' }
          ];
        return Mock.reply(200, { suggestions: list }, 550);
      }
      if (method === 'POST' && p === '/v1/checks') {
        var b = body || {};
        if (!b.leadToken || !(db.leads[b.leadToken] || b.leadToken === 'lt_demo')) return Mock.err(401, 'invalid_token', 'Invalid lead token');
        if (!isUrl(b.website)) return Mock.err(400, 'invalid_url', COPY.errors.url);
        if (!b.businessName || !b.city || !b.category) return Mock.err(400, 'missing_fields', 'Missing fields');
        db.attempts[sc] = (db.attempts[sc] || 0) + 1;
        Mock.save(db);
        if (sc === 'badbot' && db.attempts[sc] === 1) return Mock.err(403, 'turnstile_failed', COPY.errors.bot);
        if (sc === 'error' && db.attempts[sc] === 1) return Mock.err(500, 'server_error', 'Upstream failure');
        var bizIn = { name: b.businessName, website: b.website, city: b.city, category: b.category };
        if (sc === 'limit' || /limit/i.test(b.website)) {
          db.checks.ck_456 = { createdAt: Date.now() - 999999, band: 'low', business: bizIn, deferred: false };
          Mock.save(db);
          var em = db.leads[b.leadToken] || 'dan@northlineplumbing.ca';
          var tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(9, 0, 0, 0);
          return Mock.err(429, 'limit_reached', 'One free check per business per day', { existingCheckId: 'ck_456', maskedEmail: maskEmail(em), retryAfter: tomorrow.toISOString() });
        }
        var id = 'ck_' + Math.random().toString(36).slice(2, 8);
        db.checks[id] = {
          createdAt: Date.now(),
          band: /^(low|mid|high)$/.test(sc) ? sc : 'low',
          business: bizIn,
          deferred: sc === 'deferred' || /timeout/i.test(b.website)
        };
        Mock.save(db);
        return Mock.reply(202, { checkId: id, status: 'queued', pollAfterMs: 2000 }, 700);
      }
      if (method === 'GET' && (m = p.match(/^\/v1\/checks\/([\w-]+)$/))) {
        var ck = db.checks[m[1]];
        if (!ck) return Mock.err(404, 'not_found', 'Unknown check');
        var keys = ['chatgpt', 'gemini', 'perplexity', 'gbp', 'scoring'];
        var bounds = [0.24, 0.46, 0.66, 0.84, 1];
        var D = CFG.mockDurationMs, e = Date.now() - ck.createdAt, f = Math.min(1, e / D);
        if (ck.deferred) {
          if (e > D * 0.62) return Mock.reply(200, { status: 'deferred', progress: { currentStep: 'perplexity', stepsDone: ['chatgpt', 'gemini'], percent: 52 }, result: null }, 250);
          f = Math.min(f, 0.52);
        }
        var done = keys.filter(function (k, i) { return bounds[i] <= f; });
        if (f >= 1) {
          return Mock.reply(200, { status: 'complete', progress: { currentStep: 'scoring', stepsDone: keys.slice(), percent: 100 }, result: buildResult(ck.band, ck.business, m[1]) }, 250);
        }
        return Mock.reply(200, {
          status: e < 900 ? 'queued' : 'running',
          progress: { currentStep: keys[done.length] || 'scoring', stepsDone: done, percent: Math.max(2, Math.min(99, Math.round(f * 100))) },
          result: null
        }, 250);
      }
      if (method === 'GET' && (m = p.match(/^\/v1\/results\/([\w-]+)$/))) {
        var sid = m[1];
        var band = sid === FIXTURES.mid.shareId ? 'mid' : sid === FIXTURES.high.shareId ? 'high' : sid === FIXTURES.low.shareId ? 'low' : (/^(low|mid|high)$/.test(sc) ? sc : null);
        if (!band) return Mock.err(404, 'not_found', 'Unknown share');
        return Mock.reply(200, buildResult(band, DEFAULT_BIZ, 'ck_shared'), 450);
      }
      if (method === 'POST' && p === '/v1/reminders') {
        var dt = new Date(); dt.setDate(dt.getDate() + 30);
        return Mock.reply(201, { remindAt: dt.toISOString().slice(0, 10) }, 500);
      }
      return Mock.err(404, 'not_found', 'No mock for ' + method + ' ' + p);
    }
  };

  /* ===================================================================
     6. API CLIENT
     Every call resolves (never rejects) to { ok, status, data }.
     Network failures come back as status 0 with error.code "network".
     =================================================================== */
  var Api = {
    request: function (method, path, body) {
      if (CFG.mock) return Mock.handle(method, path, body);
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, CFG.requestTimeoutMs) : null;
      var opts = { method: method, headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit' };
      if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
      if (ctrl) opts.signal = ctrl.signal;
      return fetch(String(CFG.apiBase).replace(/\/+$/, '') + path, opts)
        .then(function (res) {
          return res.text().then(function (txt) {
            var data = {};
            try { data = txt ? JSON.parse(txt) : {}; } catch (e) { data = {}; }
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .catch(function () { return { ok: false, status: 0, data: { error: { code: 'network', message: 'Network error' } } }; })
        .then(function (r) { if (timer) clearTimeout(timer); return r; });
    },
    code: function (r) { return (r && r.data && r.data.error && r.data.error.code) || ''; },
    createLead: function (email, source) {
      return Api.request('POST', '/v1/leads', {
        email: String(email).trim(),
        consent: { given: true, text: COPY.consent.text + ' ' + COPY.consent.privacy, at: new Date().toISOString(), pageUrl: window.location.href },
        source: source,
        utm: utm()
      });
    },
    getLead: function (lt) { return Api.request('GET', '/v1/leads/me?lt=' + encodeURIComponent(lt)); },
    suggest: function (website) { return Api.request('GET', '/v1/suggest?url=' + encodeURIComponent(cleanUrl(website))); },
    createCheck: function (payload) { return Api.request('POST', '/v1/checks', payload); },
    getCheck: function (id) { return Api.request('GET', '/v1/checks/' + encodeURIComponent(id)); },
    getResult: function (shareId) { return Api.request('GET', '/v1/results/' + encodeURIComponent(shareId)); },
    createReminder: function (lt, checkId) { return Api.request('POST', '/v1/reminders', { leadToken: lt, checkId: checkId }); }
  };

  /* ===================================================================
     7. TURNSTILE (managed, interaction-only)
     =================================================================== */
  var Turnstile = {
    token: null, widgetId: null, loader: null,
    enabled: function () { return !CFG.mock && CFG.turnstileSiteKey && !/^\[/.test(CFG.turnstileSiteKey); },
    load: function () {
      if (Turnstile.loader) return Turnstile.loader;
      Turnstile.loader = new Promise(function (resolve) {
        if (window.turnstile) return resolve();
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true; s.defer = true;
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };
        document.head.appendChild(s);
      });
      return Turnstile.loader;
    },
    mount: function (el) {
      if (!el || !Turnstile.enabled()) return;
      Turnstile.load().then(function () {
        if (!window.turnstile || Turnstile.widgetId !== null) return;
        try {
          Turnstile.widgetId = window.turnstile.render(el, {
            sitekey: CFG.turnstileSiteKey,
            appearance: 'interaction-only',
            theme: 'dark',
            callback: function (t) { Turnstile.token = t; },
            'expired-callback': function () { Turnstile.token = null; },
            'error-callback': function (code) { Turnstile.token = null; try { console.warn('[bz-ai-score] Turnstile error', code); } catch (e) { /* ignore */ } }
          });
        } catch (e) { /* render failed; backend will answer 403 and we show the bot message */ }
      });
    },
        getToken: function () {
      if (CFG.mock) return Promise.resolve('mock-turnstile-token');
      if (!Turnstile.enabled()) return Promise.resolve('');
      var start = Date.now();
      return new Promise(function (resolve) {
        (function poll() {
          var t = Turnstile.token;
          if (!t && window.turnstile && Turnstile.widgetId !== null) {
            try { t = window.turnstile.getResponse(Turnstile.widgetId) || null; } catch (e) { /* ignore */ }
          }
          if (t) return resolve(t);
          if (Date.now() - start > 20000) return resolve('');
          setTimeout(poll, 250);
        })();
      });
    },
    reset: function () {
      Turnstile.token = null;
      try { if (window.turnstile && Turnstile.widgetId !== null) window.turnstile.reset(Turnstile.widgetId); } catch (e) { /* ignore */ }
    }
  };

  /* ===================================================================
     8. OFFERS (front end decides from the score)
     =================================================================== */
  var Offers = {
    band: function (score) {
      var s = Number(score) || 0;
      if (s <= CFG.bands.lowMax) return 'low';
      if (s <= CFG.bands.midMax) return 'mid';
      return 'high';
    },
    plan: function (band) {
      if (band === 'high') return { tag: COPY.cta.tagHigh, highlight: false, title: COPY.offers.titleHigh, cards: ['growthCare', 'localSeo'], rec: 'growthCare' };
      if (band === 'mid') return { tag: COPY.cta.tagMid, highlight: true, title: COPY.offers.titleMid, cards: ['localSeo', 'growthCare'], rec: null };
      return { tag: COPY.cta.tagLow, highlight: true, title: COPY.offers.titleLow, cards: ['localSeo', 'seoAudit'], rec: null };
    },
    card: function (key, recommended) {
      var c = COPY.offers[key];
      var price = fmt(c.price, { growthCarePrice: CFG.growthCarePrice });
      return '' +
        '<article class="svc-card' + (recommended ? ' svc-card--rec' : '') + '">' +
          '<div class="svc-card__top">' +
            '<span class="svc-card__cat">' + esc(c.cat) + '</span>' +
            '<span class="svc-card__price"><span class="svc-card__now">' + price + (c.unit ? '<small>' + esc(c.unit) + '</small>' : '') + '</span>' +
            (c.old ? '<span class="svc-card__old">' + esc(c.old) + '</span>' : '') + '</span>' +
          '</div>' +
          (recommended ? '<span class="svc-card__badge">' + icon('star') + esc(COPY.offers.recommended) + '</span>' : '') +
          '<h4 class="svc-card__title">' + esc(c.title) + '</h4>' +
          '<p class="svc-card__desc">' + esc(c.desc) + '</p>' +
          '<p class="svc-card__del">' + esc(c.del) + '</p>' +
          '<a class="svc-card__btn" href="' + esc(productUrl(key)) + '" data-bzs-cta="' + key + '|offers" aria-label="' + esc(COPY.offers.button + ': ' + c.title) + '">' + esc(COPY.offers.button) + '</a>' +
        '</article>';
    },
    section: function (band) {
      var plan = Offers.plan(band);
      return '' +
        '<section class="bzs-offers" aria-labelledby="bzs-offers-title">' +
          '<div class="bzs-offers__head"><h3 class="bzs-block__title" id="bzs-offers-title">' + esc(plan.title) + '</h3><p class="bzs-block__sub">' + esc(COPY.offers.sub) + '</p></div>' +
          '<div class="bzs-svc-row' + (plan.cards.length > 2 ? ' bzs-svc-row--3' : '') + '">' +
            plan.cards.map(function (k) { return Offers.card(k, plan.rec === k); }).join('') +
          '</div>' +
          '<p class="bzs-swipe" aria-hidden="true">' + esc(COPY.offers.swipe) + '</p>' +
        '</section>';
    }
  };

  /* ===================================================================
     9. RESULTS RENDERER (renders only from the API RESULT object)
     =================================================================== */
  var RING_R = 64, RING_C = 2 * Math.PI * RING_R;
  function gradeColor(g) {
    return { A: '#2FD36B', B: '#2FD36B', C: '#f5b23c', D: '#f5803c', F: '#f45759' }[String(g).toUpperCase()] || '#3aa0ff';
  }

  var Results = {
    orderCard: function (band) {
      var plan = Offers.plan(band), c = COPY.cta;
      return '' +
        '<div class="bzs-card bzs-order' + (plan.highlight ? ' bzs-order--hl' : '') + '">' +
          '<span class="bzs-order__tag">' + esc(plan.tag) + '</span>' +
          '<h3 class="bzs-order__title">' + esc(c.title) + '</h3>' +
          '<p class="bzs-order__line">' + esc(c.line) + '</p>' +
          '<div><p class="bzs-order__label">' + esc(c.includedLabel) + '</p>' +
            '<ul class="bzs-list" style="margin-top:12px">' + c.included.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul></div>' +
          '<dl class="bzs-order__rows" style="margin:0">' +
            '<div class="bzs-order__row"><dt>' + esc(c.delivery) + '</dt><dd>' + esc(CFG.auditDelivery) + '</dd></div>' +
            '<div class="bzs-order__row"><dt>' + esc(c.payment) + '</dt><dd>' + esc(c.paymentValue) + '</dd></div>' +
          '</dl>' +
          '<div class="bzs-order__total"><span class="bzs-order__total-label">' + esc(c.total) + '</span><span class="bzs-order__price">' + esc(c.price) + '</span></div>' +
          '<a class="bzs-btn bzs-btn--block" href="' + esc(productUrl('audit')) + '" data-bzs-cta="audit|results_card">' + esc(c.button) + '</a>' +
          '<p class="bzs-order__fine">' + esc(c.fine) + '</p>' +
          '<p class="bzs-order__spots">' + icon('clock') + esc(c.spots) + '</p>' +
          '<p class="bzs-trust">' + c.trust.map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') + '</p>' +
        '</div>';
    },

    scorePanel: function (r, band) {
      var R = COPY.results, st = r.stats || {};
      var color = gradeColor(r.grade);
      return '' +
        '<div class="bzs-card bzs-score">' +
          '<div class="bzs-score__top">' +
            '<div class="bzs-ring" role="img" aria-label="' + esc(fmt(R.ringLabel, { score: r.score, grade: r.grade }, true)) + '">' +
              '<svg viewBox="0 0 148 148" aria-hidden="true"><circle class="bzs-ring__track" cx="74" cy="74" r="' + RING_R + '" fill="none" stroke-width="12"/>' +
              '<circle class="bzs-ring__val" cx="74" cy="74" r="' + RING_R + '" fill="none" stroke="' + color + '" stroke-width="12" stroke-linecap="round" stroke-dasharray="' + RING_C.toFixed(2) + '" stroke-dashoffset="' + RING_C.toFixed(2) + '" data-target="' + (RING_C * (1 - Math.max(0, Math.min(100, r.score)) / 100)).toFixed(2) + '"/></svg>' +
              '<div class="bzs-ring__num" aria-hidden="true"><span class="bzs-ring__big" data-count="' + esc(r.score) + '">' + esc(r.score) + '</span><span class="bzs-ring__of">' + esc(R.of100) + '</span></div>' +
            '</div>' +
            '<div class="bzs-score__body">' +
              '<div class="bzs-score__badges">' +
                '<span class="bzs-grade" data-grade="' + esc(String(r.grade).toUpperCase()) + '" aria-label="' + esc(R.gradeLabel + ' ' + r.grade) + '">' + esc(r.grade) + '</span>' +
                '<span class="bzs-gradelabel" aria-hidden="true">' + esc(R.gradeLabel) + '</span>' +
                '<span class="bzs-tag" data-band="' + band + '">' + icon(band === 'high' ? 'check' : band === 'mid' ? 'warn' : 'alert') + esc(R.tags[band]) + '</span>' +
              '</div>' +
              '<h3 class="bzs-score__headline">' + esc(r.summary && r.summary.headline) + '</h3>' +
              '<p class="bzs-score__text">' + esc(r.summary && r.summary.text) + '</p>' +
            '</div>' +
          '</div>' +
          '<div class="bzs-stats">' +
            Results.stat(R.stats.namedIn, fmt(R.stats.namedInValue, { n: st.namedIn, t: st.totalAnswers })) +
            Results.stat(R.stats.tools, esc(st.toolsChecked)) +
            Results.stat(R.stats.competitors, esc(st.competitorsFound)) +
            Results.stat(R.stats.fixes, esc(st.quickFixes)) +
          '</div>' +
        '</div>';
    },
    stat: function (label, valueHtml) {
      return '<div class="bzs-stat"><span class="bzs-stat__label">' + esc(label) + '</span><span class="bzs-stat__value">' + valueHtml + '</span></div>';
    },

    breakdown: function (r) {
      var R = COPY.results;
      return '' +
        '<section class="bzs-card" aria-labelledby="bzs-bd-title">' +
          '<div class="bzs-block__head"><h3 class="bzs-block__title" id="bzs-bd-title">' + esc(R.breakdownTitle) + '</h3><p class="bzs-block__sub">' + esc(R.breakdownSub) + '</p></div>' +
          '<ul class="bzs-sources" style="list-style:none;padding:0;margin:0">' +
            (r.sources || []).map(function (s) {
              var st = /^(pass|warning|fail)$/.test(s.status) ? s.status : 'warning';
              return '<li class="bzs-source">' +
                '<div class="bzs-source__top"><span class="bzs-source__icon">' + icon(SOURCE_ICON[s.id] || 'globe') + '</span>' +
                '<span class="bzs-status" data-status="' + st + '">' + icon(STATUS_ICON[st]) + esc(R.status[st]) + '</span></div>' +
                '<h4 class="bzs-source__name">' + esc(s.label) + '</h4>' +
                '<p class="bzs-source__metric">' + esc(s.metric) + '</p>' +
                '<p class="bzs-source__note">' + esc(s.note) + '</p>' +
              '</li>';
            }).join('') +
          '</ul>' +
        '</section>';
    },

    competitors: function (r) {
      var R = COPY.results, c = r.competitors || { visible: [], lockedCount: 0 }, b = r.business || {};
      var vis = (c.visible || []).slice(0, 2);
      var rows = vis.length ? vis.map(function (v, i) {
        return '<li class="bzs-comp__row"><span class="bzs-comp__rank">' + (i + 1) + '</span><div><p class="bzs-comp__name">' + esc(v.name) + '</p><p class="bzs-comp__reason">' + esc(v.reason) + '</p></div></li>';
      }).join('') : '<li class="bzs-comp__row"><span class="bzs-comp__rank">0</span><p class="bzs-comp__reason">' + esc(R.compNone) + '</p></li>';
      var n = Number(c.lockedCount) || 0, locked = '';
      if (n > 0) {
        var fake = '';
        for (var i = 0; i < Math.min(n, 4); i++) {
          fake += '<li class="bzs-comp__row"><span class="bzs-comp__rank">' + (vis.length + i + 1) + '</span><div><p class="bzs-comp__name">' + FAKE_LOCKED[i % FAKE_LOCKED.length] + '</p><p class="bzs-comp__reason">Named by ChatGPT and Gemini</p></div></li>';
        }
        locked = '<div class="bzs-comp__locked"><ul class="bzs-comp" aria-hidden="true">' + fake + '</ul>' +
          '<div class="bzs-comp__unlock"><a href="' + esc(productUrl('audit')) + '" data-bzs-cta="audit|competitors_unlock">' + icon('lock') + esc(fmt(COPY.results.unlock, { n: n }, true)) + '</a></div></div>';
      }
      return '' +
        '<section class="bzs-card" aria-labelledby="bzs-comp-title">' +
          '<div class="bzs-block__head"><h3 class="bzs-block__title" id="bzs-comp-title">' + esc(R.compTitle) + '</h3>' +
          '<p class="bzs-block__sub">' + fmt(R.compSub, { category: String(b.category || '').toLowerCase(), city: b.city }) + '</p></div>' +
          '<ul class="bzs-comp">' + rows + '</ul>' + locked +
        '</section>';
    },

    problems: function (r) {
      var R = COPY.results;
      return '' +
        '<section class="bzs-card" aria-labelledby="bzs-pr-title">' +
          '<div class="bzs-block__head"><h3 class="bzs-block__title" id="bzs-pr-title">' + esc(R.problemsTitle) + '</h3><p class="bzs-block__sub">' + esc(R.problemsSub) + '</p></div>' +
          '<ol class="bzs-problems">' +
            (r.problems || []).slice(0, 3).map(function (p, i) {
              var sev = /^(high|medium|low)$/.test(p.severity) ? p.severity : 'medium';
              return '<li class="bzs-problem"><span class="bzs-problem__num" aria-hidden="true">' + (i + 1) + '</span><div>' +
                '<p class="bzs-problem__title"><span>' + esc(p.title) + '</span><span class="bzs-sev" data-sev="' + sev + '">' + esc(R.severity[sev]) + '</span></p>' +
                '<p class="bzs-problem__detail">' + esc(p.detail) + '</p></div></li>';
            }).join('') +
          '</ol>' +
        '</section>';
    },

    share: function (r) {
      var S = COPY.share, g = String(r.grade).toUpperCase();
      var url = (r.share && r.share.url) || '';
      return '' +
        '<section class="bzs-card bzs-share" aria-labelledby="bzs-share-title">' +
          '<h3 class="bzs-block__title" id="bzs-share-title">' + esc(S.title) + '</h3>' +
          '<div class="bzs-badgeprev" role="img" aria-label="' + esc(fmt(S.badgeAria, { grade: g, score: r.score }, true)) + '">' +
            '<span class="bzs-grade" data-grade="' + esc(g) + '" aria-hidden="true">' + esc(g) + '</span>' +
            '<div aria-hidden="true"><p class="bzs-badgeprev__label">' + esc(S.badgeLabel) + '</p><p class="bzs-badgeprev__score">' + esc(r.score) + '/100</p><p class="bzs-badgeprev__by">' + esc(S.badgeBy) + '</p></div>' +
          '</div>' +
          '<div class="bzs-btnrow">' +
            '<button type="button" class="bzs-pill bzs-pill--ghost" data-bzs-copy>' + icon('link') + '<span>' + esc(S.copy) + '</span></button>' +
            '<a class="bzs-pill bzs-pill--ghost" href="' + esc((r.share && r.share.badgeUrl) || '#') + '" download="' + esc(S.fileName) + '" data-bzs-download>' + icon('download') + '<span>' + esc(S.download) + '</span></a>' +
          '</div>' +
          '<p class="bzs-shareurl">' + esc(url) + '</p>' +
        '</section>';
    },

    recheck: function () {
      var C = COPY.recheck;
      return '' +
        '<section class="bzs-card bzs-recheck" aria-labelledby="bzs-rc-title">' +
          '<span class="bzs-recheck__icon">' + icon('bell') + '</span>' +
          '<h3 class="bzs-block__title" id="bzs-rc-title">' + esc(C.title) + '</h3>' +
          '<p class="bzs-small">' + esc(C.text) + '</p>' +
          '<div data-bzs-remind-slot><button type="button" class="bzs-btn bzs-btn--block bzs-btn--ghost" data-bzs-remind>' + icon('calendar') + '<span>' + esc(C.button) + '</span></button></div>' +
          '<p class="bzs-small">' + esc(C.fine) + '</p>' +
        '</section>';
    },

    /* opts: { shared: bool, email: string } */
    html: function (r, opts) {
      opts = opts || {};
      var R = COPY.results, b = r.business || {}, band = Offers.band(r.score);
      var meta = [b.category, b.city, b.website, fmt(R.checked, { date: fmtDate(r.checkedAt) }, true)].filter(Boolean);
      var out = '';
      if (!opts.shared && opts.email) {
        out += '<div class="bzs-note" role="status"><span class="bzs-note__icon">' + icon('check') + '</span><span class="bzs-note__text">' + fmt(R.emailed, { email: opts.email }) + '</span></div>';
      }
      out += '<header class="bzs-results__head">' +
        '<span class="bzs-eyebrow">' + esc(opts.shared ? R.eyebrowShared : R.eyebrow) + '</span>' +
        '<h2 class="bzs-results__name" tabindex="-1" data-bzs-results-title>' + esc(b.name) + '</h2>' +
        '<p class="bzs-results__meta">' + meta.map(function (m) { return '<span>' + esc(m) + '</span>'; }).join('') + '</p>' +
      '</header>';
      out += '<div class="bzs-rgrid' + (opts.shared ? ' bzs-rgrid--solo' : '') + '">';
      out += Results.scorePanel(r, band);
      if (!opts.shared) out += '<aside class="bzs-raside" aria-label="' + esc(COPY.cta.title) + '">' + Results.orderCard(band) + '</aside>';
      out += '<div class="bzs-rmain">';
      out += Results.breakdown(r);
      if (!opts.shared) out += Results.competitors(r);
      out += Results.problems(r);
      if (!opts.shared) {
        out += Offers.section(band);
        out += '<div class="bzs-duo">' + Results.share(r) + Results.recheck() + '</div>';
      }
      out += '</div></div>';
      return out;
    },

    animate: function (root) {
      var ring = $('.bzs-ring__val', root), num = $('.bzs-ring__big', root);
      if (!ring) return;
      var target = ring.getAttribute('data-target');
      if (reducedMotion()) { ring.setAttribute('stroke-dashoffset', target); return; }
      requestAnimationFrame(function () { requestAnimationFrame(function () { ring.setAttribute('stroke-dashoffset', target); }); });
      if (num) {
        var end = Number(num.getAttribute('data-count')) || 0, t0 = null;
        var step = function (ts) {
          if (!t0) t0 = ts;
          var p = Math.min(1, (ts - t0) / 1100);
          num.textContent = String(Math.round(end * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        num.textContent = '0';
        requestAnimationFrame(step);
      }
    }
  };

  /* ===================================================================
     10. SHARE (copy link, badge download, re-check reminder)
     =================================================================== */
  var Share = {
    copy: function (btn, url) {
      var done = function () {
        btn.setAttribute('data-copied', 'true');
        var label = $('span', btn); if (label) label.textContent = COPY.share.copied;
        Analytics.push('bzs_share_copy');
        setTimeout(function () { btn.removeAttribute('data-copied'); if (label) label.textContent = COPY.share.copy; }, 2500);
      };
      var fallback = function () {
        try {
          var ta = document.createElement('textarea');
          ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        } catch (e) { /* ignore */ }
        done();
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url).then(done, fallback);
      else fallback();
    },
    /* Draws the badge locally (used in mock mode or when the API sends no badgeUrl). */
    badgePng: function (r) {
      try {
        var cv = document.createElement('canvas'), W = 600, H = 200, x = cv.getContext('2d');
        cv.width = W; cv.height = H;
        var g = x.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#13233d'); g.addColorStop(1, '#0d0e10');
        x.fillStyle = g; roundRect(x, 0, 0, W, H, 24); x.fill();
        x.strokeStyle = 'rgba(58,160,255,.45)'; x.lineWidth = 2; roundRect(x, 1, 1, W - 2, H - 2, 23); x.stroke();
        x.fillStyle = gradeColor(r.grade); roundRect(x, 36, 44, 112, 112, 22); x.fill();
        x.fillStyle = '#0b0b0d'; x.font = '800 64px Inter, Arial, sans-serif'; x.textAlign = 'center'; x.textBaseline = 'middle';
        x.fillText(String(r.grade), 92, 103);
        x.textAlign = 'left'; x.textBaseline = 'alphabetic';
        x.fillStyle = '#8fc6ff'; x.font = '700 18px "JetBrains Mono", monospace'; x.fillText('AI VISIBILITY SCORE', 180, 78);
        x.fillStyle = '#ffffff'; x.font = '700 52px Inter, Arial, sans-serif'; x.fillText(r.score + '/100', 180, 132);
        x.fillStyle = '#a1a1a1'; x.font = '500 18px Inter, Arial, sans-serif'; x.fillText('Checked by BoldZee', 180, 162);
        return cv.toDataURL('image/png');
      } catch (e) { return ''; }
      function roundRect(c, a, b, w, h, rr) { c.beginPath(); c.moveTo(a + rr, b); c.arcTo(a + w, b, a + w, b + h, rr); c.arcTo(a + w, b + h, a, b + h, rr); c.arcTo(a, b + h, a, b, rr); c.arcTo(a, b, a + w, b, rr); c.closePath(); }
    },
    bind: function (root, r, lt) {
      var copyBtn = $('[data-bzs-copy]', root);
      if (copyBtn) copyBtn.addEventListener('click', function () { Share.copy(copyBtn, (r.share && r.share.url) || window.location.href); });
      var dl = $('[data-bzs-download]', root);
      if (dl && (CFG.mock || !(r.share && r.share.badgeUrl))) {
        var png = Share.badgePng(r);
        if (png) dl.setAttribute('href', png);
      }
      var remind = $('[data-bzs-remind]', root);
      if (remind) remind.addEventListener('click', function () {
        if (remind.getAttribute('aria-busy') === 'true') return;
        remind.setAttribute('aria-busy', 'true');
        Api.createReminder(lt, r.checkId).then(function (res) {
          var slot = $('[data-bzs-remind-slot]', root);
          if (res.ok && res.data && res.data.remindAt) {
            Analytics.push('bzs_reminder_set');
            slot.innerHTML = '<div class="bzs-note" role="status"><span class="bzs-note__icon">' + icon('check') + '</span><span class="bzs-note__text">' + fmt(COPY.recheck.done, { date: fmtDateLong(res.data.remindAt) }) + '</span></div>';
          } else {
            remind.removeAttribute('aria-busy');
            slot.insertAdjacentHTML('beforeend', '<p class="bzs-error" role="alert" style="margin-top:10px">' + icon('alert') + '<span>' + esc(COPY.errors.server) + '</span></p>');
          }
        });
      });
    }
  };

  /* ===================================================================
     11. POLLING + live check card
     =================================================================== */
  var STEP_KEYS = ['chatgpt', 'gemini', 'perplexity', 'gbp', 'scoring'];
  var Polling = {
    timer: null, factTimer: null, run: 0,
    stop: function () { clearTimeout(Polling.timer); clearInterval(Polling.factTimer); Polling.run++; },
    /* handlers: { onProgress(p), onComplete(result), onDeferred(), onFailed() } */
    start: function (checkId, pollMs, handlers) {
      Polling.stop();
      var run = Polling.run, t0 = Date.now(), every = Math.max(800, Number(pollMs) || CFG.pollDefaultMs);
      var tick = function () {
        if (run !== Polling.run) return;
        if (Date.now() - t0 > CFG.pollTimeoutMs) { Polling.stop(); handlers.onDeferred(); return; }
        Api.getCheck(checkId).then(function (res) {
          if (run !== Polling.run) return;
          var d = res.data || {};
          if (res.ok) {
            if (d.progress) handlers.onProgress(d.progress, d.status);
            if (d.status === 'complete' && d.result) { Polling.stop(); handlers.onComplete(d.result); return; }
            if (d.status === 'deferred') { Polling.stop(); handlers.onDeferred(); return; }
            if (d.status === 'failed') { Polling.stop(); handlers.onFailed(); return; }
          } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            Polling.stop(); handlers.onFailed(); return;
          }
          /* network blips and 5xx: keep polling until the 90 second timeout */
          Polling.timer = setTimeout(tick, every);
        });
      };
      Polling.timer = setTimeout(tick, Math.min(every, 1200));
    }
  };

  /* ===================================================================
     12. PAGE (/pages/ai-score)
     =================================================================== */
  var Page = {
    root: null,
    s: { lt: null, email: '', placeId: null, biz: null, checkId: null, result: null, hasResult: false, review: false, shared: false, suggestReq: 0, maxPct: 0, lastStep: '' },

    /* ---------- skeleton ---------- */
    skeleton: function () {
      var C = COPY, root = Page.root;
      var headline = root.getAttribute('data-headline') || C.intro.title;
      var subtitle = root.getAttribute('data-subtitle') || C.intro.sub;
      var tracker = C.intro.tracker.map(function (t, i) {
        return (i ? '<li class="bzs-tracker__line" aria-hidden="true" data-line="' + i + '"></li>' : '') +
          '<li class="bzs-tracker__item" data-track="' + (i + 1) + '" data-state="' + (i === 0 ? 'active' : 'pending') + '">' +
            '<span class="bzs-tracker__num" aria-hidden="true">' + (i + 1) + '</span>' +
            '<span class="bzs-tracker__label">' + esc(t) + '<span class="bzs-sr" data-track-sr></span></span>' +
          '</li>';
      }).join('');
      root.innerHTML = '' +
        '<div class="bzs-container">' +
          '<div data-bzs-shared hidden></div>' +
          '<header class="bzs-intro" id="bzs-top">' +
            '<span class="bzs-eyebrow">' + esc(C.intro.eyebrow) + '</span>' +
            '<h1 class="bzs-title">' + esc(headline) + '</h1>' +
            '<p class="bzs-sub">' + esc(subtitle) + '</p>' +
            '<div class="bzs-stats">' + C.intro.stats.map(function (s) { return Results.stat(s[0], esc(s[1])); }).join('') + '</div>' +
            '<ol class="bzs-tracker" aria-label="' + esc(C.intro.trackerLabel) + '">' + tracker + '</ol>' +
          '</header>' +
          '<div class="bzs-steps">' +
            '<section class="bzs-step" id="bzs-step1" aria-labelledby="bzs-s1-title"><div class="bzs-card">' +
              '<div class="bzs-card__head"><h2 class="bzs-card__title" id="bzs-s1-title" tabindex="-1">' + esc(C.step1.title) + '</h2></div>' +
              '<div data-s1-body></div>' +
            '</div></section>' +
            '<section class="bzs-step" id="bzs-step2" aria-labelledby="bzs-s2-title" hidden>' + Page.step2Html() + '</section>' +
            '<section class="bzs-step" id="bzs-step3" aria-labelledby="bzs-s3-title" hidden><div class="bzs-card" data-s3-card></div></section>' +
          '</div>' +
          '<div class="bzs-results" id="bzs-results" hidden></div>' +
          '<div data-bzs-review hidden></div>' +
          Page.faqHtml() +
          Page.finalHtml() +
          '<footer class="bzs-foot"><p>' + esc(C.footer.note) + '</p><nav aria-label="Footer"><a href="' + esc(CFG.privacyUrl) + '">' + esc(C.footer.privacy) + '</a><a href="' + esc(CFG.storeUrl) + '">' + esc(C.footer.store) + '</a></nav></footer>' +
        '</div>' +
        '<p class="bzs-sr" aria-live="polite" data-bzs-live></p>';
    },

    /* ---------- step 1 ---------- */
    step1Form: function (prefill, alertMsg) {
      var C = COPY;
      return '' +
        '<form class="bzs-form" novalidate data-s1-form>' +
          '<div class="bzs-field">' +
            '<label class="bzs-label" for="bzs-email">' + esc(C.step1.label) + '<span class="bzs-req" aria-hidden="true">*</span></label>' +
            '<input class="bzs-input" id="bzs-email" name="email" type="email" inputmode="email" autocomplete="email" required placeholder="' + esc(C.popup.placeholder) + '" value="' + esc(prefill || '') + '" aria-describedby="bzs-email-err">' +
            '<p class="bzs-error" id="bzs-email-err" role="alert"></p>' +
          '</div>' +
          '<div class="bzs-field">' +
            '<label class="bzs-check"><input type="checkbox" id="bzs-consent" name="consent" required aria-describedby="bzs-consent-err">' +
            '<span>' + esc(C.consent.text) + ' <a href="' + esc(CFG.privacyUrl) + '" target="_blank" rel="noopener">' + esc(C.consent.privacy) + '</a></span></label>' +
            '<p class="bzs-error" id="bzs-consent-err" role="alert"></p>' +
          '</div>' +
          '<div data-s1-alert>' + (alertMsg ? Page.alertHtml(alertMsg) : '') + '</div>' +
          '<div><button class="bzs-btn bzs-btn--block" type="submit">' + esc(C.step1.button) + '</button>' +
          '<p class="bzs-under">' + C.step1.under.map(function (u) { return '<span>' + esc(u) + '</span>'; }).join('') + '</p></div>' +
        '</form>';
    },
    step1Done: function () {
      return '<div class="bzs-note"><span class="bzs-note__icon">' + icon('check') + '</span>' +
        '<span class="bzs-note__text">' + fmt(COPY.step1.done, { email: Page.s.email }).replace(esc(Page.s.email), '<strong>' + esc(Page.s.email) + '</strong>') + '</span>' +
        '<button type="button" class="bzs-link" data-s1-change>' + esc(COPY.step1.change) + '<span class="bzs-sr"> ' + esc(COPY.step1.label) + '</span></button></div>';
    },
    renderStep1: function (mode, prefill, alertMsg) {
      var body = $('[data-s1-body]', Page.root);
      if (mode === 'done') {
        body.innerHTML = Page.step1Done();
        $('[data-s1-change]', body).addEventListener('click', function () {
          Page.renderStep1('form', Page.s.email);
          Page.track(1);
          var inp = $('#bzs-email', Page.root); if (inp) { inp.focus(); inp.select(); }
        });
      } else {
        body.innerHTML = Page.step1Form(prefill, alertMsg);
        Page.bindStep1();
      }
    },
    bindStep1: function () {
      var form = $('[data-s1-form]', Page.root);
      var email = $('#bzs-email', form), consent = $('#bzs-consent', form);
      email.addEventListener('input', function () { Page.fieldError(email, ''); });
      consent.addEventListener('change', function () { Page.fieldError(consent, ''); });
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var ok = true;
        if (!isEmail(email.value)) { Page.fieldError(email, COPY.errors.email); ok = false; }
        if (!consent.checked) { Page.fieldError(consent, COPY.errors.consent); ok = false; }
        if (!ok) { (isEmail(email.value) ? consent : email).focus(); return; }
        var btn = $('button[type="submit"]', form);
        Page.busy(btn, true, COPY.popup.sending);
        $('[data-s1-alert]', form).innerHTML = '';
        Api.createLead(email.value, 'page').then(function (res) {
          Page.busy(btn, false, COPY.step1.button);
          if (res.ok && res.data.leadToken) {
            Analytics.push('bzs_lead_submitted', { source: 'page' });
            Page.leadDone(res.data.leadToken, res.data.email || email.value.trim(), true);
          } else {
            var code = Api.code(res);
            if (code === 'invalid_email') { Page.fieldError(email, COPY.errors.email); email.focus(); }
            else if (code === 'consent_required') { Page.fieldError(consent, COPY.errors.consent); consent.focus(); }
            else $('[data-s1-alert]', form).innerHTML = Page.alertHtml(COPY.errors.server);
          }
        });
      });
    },
    leadDone: function (lt, email, scroll) {
      Page.s.lt = lt; Page.s.email = email;
      store.set('sessionStorage', KEYS.lead, { lt: lt, email: email });
      store.set('localStorage', KEYS.popupDone, 1);
      try {
        var u = new URL(window.location.href); u.searchParams.set('lt', lt);
        window.history.replaceState(null, '', u.toString());
      } catch (e) { /* ignore */ }
      Page.renderStep1('done');
      Page.showStep(2, scroll);
    },

    /* ---------- step 2 ---------- */
    step2Html: function () {
      var C = COPY.step2;
      var field = function (id, label, ph, auto, extra) {
        return '<div class="bzs-field">' +
          '<label class="bzs-label" for="' + id + '">' + esc(label) + '<span class="bzs-req" aria-hidden="true">*</span></label>' +
          '<input class="bzs-input" id="' + id + '" type="text" required autocomplete="' + auto + '" placeholder="' + esc(ph) + '" aria-describedby="' + id + '-err"' + (extra || '') + '>' +
          '<p class="bzs-error" id="' + id + '-err" role="alert"></p></div>';
      };
      return '' +
        '<div class="bzs-card">' +
          '<div class="bzs-card__head"><h2 class="bzs-card__title" id="bzs-s2-title" tabindex="-1">' + esc(C.title) + '</h2><p class="bzs-card__sub">' + esc(C.sub) + '</p></div>' +
          '<div class="bzs-split">' +
            '<form class="bzs-split__main bzs-form" novalidate data-s2-form>' +
              '<div class="bzs-field">' +
                '<label class="bzs-label" for="bzs-website">' + esc(C.website) + '<span class="bzs-req" aria-hidden="true">*</span></label>' +
                '<div class="bzs-prefix"><span class="bzs-prefix__box" aria-hidden="true">https://</span>' +
                '<input class="bzs-input" id="bzs-website" type="text" inputmode="url" autocomplete="url" autocapitalize="off" spellcheck="false" required placeholder="' + esc(C.websitePh) + '" aria-describedby="bzs-website-err"></div>' +
                '<p class="bzs-error" id="bzs-website-err" role="alert"></p>' +
              '</div>' +
              '<div class="bzs-suggest" data-s2-suggest hidden aria-live="polite"></div>' +
              '<div class="bzs-grid-2">' +
                field('bzs-name', C.name, C.namePh, 'organization') +
                field('bzs-city', C.city, C.cityPh, 'address-level2') +
              '</div>' +
              field('bzs-category', C.category, C.categoryPh, 'off') +
              '<p class="bzs-why">' + icon('info') + '<span><strong>' + esc(C.whyLead) + '</strong> ' + esc(C.why) + '</span></p>' +
              '<div class="bzs-turnstile" data-s2-turnstile></div>' +
              '<div data-s2-alert></div>' +
              '<div><button class="bzs-btn bzs-btn--block" type="submit" data-s2-submit>' + esc(C.button) + '</button>' +
              '<p class="bzs-under">' + C.under.map(function (u) { return '<span>' + esc(u) + '</span>'; }).join('') + '</p></div>' +
            '</form>' +
            '<aside class="bzs-split__side bzs-side" aria-labelledby="bzs-wwc">' +
              '<h3 class="bzs-side-title" id="bzs-wwc">' + esc(C.sideTitle) + '</h3>' +
              '<ul class="bzs-checklist">' + C.checks.map(function (c) {
                return '<li><span class="bzs-checklist__icon">' + icon(c[0]) + '</span><div><p class="bzs-checklist__name">' + esc(c[1]) + '</p><p class="bzs-checklist__text">' + esc(c[2]) + '</p></div></li>';
              }).join('') + '</ul>' +
            '</aside>' +
          '</div>' +
        '</div>';
    },
    bindStep2: function () {
      var form = $('[data-s2-form]', Page.root);
      var web = $('#bzs-website', form), name = $('#bzs-name', form), city = $('#bzs-city', form), cat = $('#bzs-category', form);
      var box = $('[data-s2-suggest]', form), debounce = null;
      Turnstile.mount($('[data-s2-turnstile]', form));

      [name, city, cat].forEach(function (f) { f.addEventListener('input', function () { Page.fieldError(f, ''); }); });
      name.addEventListener('input', function () { if (name.value.trim()) box.hidden = true; });

      web.addEventListener('input', function () {
        Page.fieldError(web, '');
        if (/^https?:\/\//i.test(web.value)) web.value = web.value.replace(/^https?:\/\//i, '');
        Page.s.placeId = null;
        clearTimeout(debounce);
        box.hidden = true;
        if (!isUrl(web.value)) return;
        debounce = setTimeout(function () {
          if (name.value.trim()) return;
          var req = ++Page.s.suggestReq;
          Api.suggest(web.value).then(function (res) {
            if (req !== Page.s.suggestReq || name.value.trim()) return;
            var list = res.ok && res.data && Array.isArray(res.data.suggestions) ? res.data.suggestions.slice(0, 3) : [];
            if (!list.length) { box.hidden = true; return; }
            Page.renderSuggest(box, list, { name: name, city: city, cat: cat });
          });
        }, 600);
      });
      web.addEventListener('blur', function () { if (web.value) web.value = cleanUrl(web.value); });

      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        Page.submitCheck();
      });
    },
    renderSuggest: function (box, list, f) {
      var C = COPY.step2;
      box.innerHTML = '<p class="bzs-suggest__title">' + icon('pin') + esc(C.suggestTitle) + '</p>' +
        '<ul class="bzs-suggest__list">' + list.map(function (s, i) {
          return '<li class="bzs-suggest__item"><div class="bzs-suggest__meta"><span class="bzs-suggest__name">' + esc(s.name) + '</span>' +
            '<span class="bzs-suggest__addr">' + esc([s.category, s.address, s.city].filter(Boolean).join(' · ')) + '</span></div>' +
            '<button type="button" class="bzs-suggest__use" data-i="' + i + '" aria-label="' + esc(C.suggestUse + ': ' + s.name) + '">' + esc(C.suggestUse) + '</button></li>';
        }).join('') + '</ul>' +
        '<p class="bzs-suggest__foot">' + esc(C.suggestFoot) + '</p>';
      box.hidden = false;
      $$('.bzs-suggest__use', box).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var s = list[Number(btn.getAttribute('data-i'))];
          f.name.value = s.name || ''; f.city.value = s.city || ''; f.cat.value = s.category || '';
          Page.s.placeId = s.placeId || null;
          [f.name, f.city, f.cat].forEach(function (x) { Page.fieldError(x, ''); });
          box.hidden = true;
          f.cat.focus();
        });
      });
    },
    readBiz: function () {
      return {
        website: cleanUrl($('#bzs-website', Page.root).value),
        name: $('#bzs-name', Page.root).value.trim(),
        city: $('#bzs-city', Page.root).value.trim(),
        category: $('#bzs-category', Page.root).value.trim()
      };
    },
    validateBiz: function (b) {
      var E = COPY.errors, first = null, check = function (sel, ok, msg) {
        var el = $(sel, Page.root);
        if (!ok) { Page.fieldError(el, msg); if (!first) first = el; } else Page.fieldError(el, '');
      };
      check('#bzs-website', isUrl(b.website), E.url);
      check('#bzs-name', !!b.name, E.name);
      check('#bzs-city', !!b.city, E.city);
      check('#bzs-category', !!b.category && b.category.length <= 60, E.category);
      if (first) first.focus();
      return !first;
    },
    submitCheck: function () {
      var b = Page.readBiz();
      var alertBox = $('[data-s2-alert]', Page.root), btn = $('[data-s2-submit]', Page.root);
      alertBox.innerHTML = '';
      if (!Page.validateBiz(b)) return;
      if (!Page.s.lt) { Page.renderStep1('form', Page.s.email, COPY.errors.token); Page.track(1); scrollToEl($('#bzs-step1')); return; }
      Page.s.biz = b;
      Page.busy(btn, true, COPY.step2.sending);
      Turnstile.getToken().then(function (token) {
        return Api.createCheck({
          leadToken: Page.s.lt, website: b.website, businessName: b.name, city: b.city, category: b.category,
          placeId: Page.s.placeId || null, turnstileToken: token
        });
      }).then(function (res) {
        Page.busy(btn, false, Page.s.hasResult ? COPY.step2.buttonAgain : COPY.step2.button);
        Turnstile.reset();
        var code = Api.code(res);
        if (res.ok && res.data.checkId) {
          Analytics.push('bzs_check_started');
          Page.startCheck(res.data.checkId, res.data.pollAfterMs);
          return;
        }
        if (res.status === 400 && code === 'invalid_url') { Page.fieldError($('#bzs-website', Page.root), COPY.errors.url); $('#bzs-website', Page.root).focus(); return; }
        if (res.status === 400) { Page.validateBiz(b); return; }
        if (res.status === 401) {
          Page.s.lt = null;
          Page.renderStep1('form', Page.s.email, COPY.errors.token); Page.track(1); scrollToEl($('#bzs-step1'));
          return;
        }
        if (res.status === 403) { alertBox.innerHTML = Page.alertHtml(COPY.errors.bot, true); Page.bindRetry(alertBox); return; }
        if (res.status === 429 || code === 'limit_reached') { Analytics.push('bzs_limit_reached'); Page.showLimit(res.data || {}); return; }
        alertBox.innerHTML = Page.alertHtml(COPY.errors.server, true);
        Page.bindRetry(alertBox);
      });
    },
    bindRetry: function (scope) {
      var b = $('[data-bzs-retry]', scope);
      if (b) b.addEventListener('click', function () { Page.submitCheck(); });
      var a = $('.bzs-alert', scope); if (a) focusEl(a);
    },

    /* ---------- step 3 ---------- */
    runHtml: function (b) {
      var C = COPY.step3;
      return '' +
        '<div class="bzs-card__head"><h2 class="bzs-card__title" id="bzs-s3-title" tabindex="-1" data-s3-title>' + esc(C.title) + '</h2>' +
        '<p class="bzs-card__sub" data-s3-sub>' + fmt(C.sub, { name: b.name, city: b.city }) + '</p></div>' +
        '<div class="bzs-progress" data-done="false">' +
          '<div class="bzs-progress__top"><span class="bzs-small">' + esc(C.progress) + '</span><span class="bzs-progress__pct" data-s3-pct>0%</span></div>' +
          '<div class="bzs-progress__track" role="progressbar" aria-label="' + esc(C.progressLabel) + '" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><div class="bzs-progress__fill"></div></div>' +
        '</div>' +
        '<ol class="bzs-runsteps">' + STEP_KEYS.map(function (k, i) {
          return '<li class="bzs-runstep" data-key="' + k + '" data-state="' + (i === 0 ? 'active' : 'pending') + '"><span class="bzs-runstep__icon"></span><span>' + esc(C.steps[k]) + '</span><span class="bzs-runstep__state"></span></li>';
        }).join('') + '</ol>' +
        '<div data-s3-extra>' +
          '<div class="bzs-fact"><span class="bzs-fact__icon">' + icon('bulb') + '</span><div><p class="bzs-fact__label">' + esc(C.factLabel) + '</p><p class="bzs-fact__text" data-s3-fact>' + esc(C.facts[0]) + '</p></div></div>' +
          '<p class="bzs-runnote">' + icon('clock') + '<span>' + esc(C.note) + '</span></p>' +
        '</div>';
    },
    setRunStep: function (li, state) {
      if (li.getAttribute('data-state') === state && li.firstChild.firstChild) return;
      li.setAttribute('data-state', state);
      var ic = $('.bzs-runstep__icon', li), lab = $('.bzs-runstep__state', li);
      if (state === 'done') { ic.innerHTML = '<span class="bzs-runstep__ok">' + icon('check') + '</span>'; lab.textContent = COPY.step3.stateDone; }
      else if (state === 'active') { ic.innerHTML = '<span class="bzs-spin" aria-hidden="true"></span>'; lab.textContent = COPY.step3.stateActive; }
      else { ic.innerHTML = '<span class="bzs-runstep__ring" aria-hidden="true"></span>'; lab.textContent = ''; }
    },
    updateProgress: function (p) {
      var card = $('[data-s3-card]', Page.root);
      var done = p.stepsDone || [], cur = p.currentStep;
      $$('.bzs-runstep', card).forEach(function (li) {
        var k = li.getAttribute('data-key');
        Page.setRunStep(li, done.indexOf(k) > -1 ? 'done' : k === cur ? 'active' : 'pending');
      });
      var pct = Math.max(Page.s.maxPct, Math.min(100, Math.round(Number(p.percent) || 0)));
      Page.s.maxPct = pct;
      $('.bzs-progress__fill', card).style.width = pct + '%';
      $('[data-s3-pct]', card).textContent = pct + '%';
      $('.bzs-progress__track', card).setAttribute('aria-valuenow', String(pct));
      if (cur && cur !== Page.s.lastStep && COPY.step3.steps[cur]) { Page.s.lastStep = cur; Page.live(COPY.step3.steps[cur]); }
    },
    startCheck: function (checkId, pollMs) {
      Page.s.checkId = checkId; Page.s.maxPct = 0; Page.s.lastStep = '';
      var card = $('[data-s3-card]', Page.root);
      card.innerHTML = Page.runHtml(Page.s.biz);
      $$('.bzs-runstep', card).forEach(function (li, i) { Page.setRunStep(li, i === 0 ? 'active' : 'pending'); li.setAttribute('data-state', i === 0 ? 'active' : 'pending'); });
      Page.showStep(3, true);
      var fi = 0, factEl = $('[data-s3-fact]', card);
      clearInterval(Polling.factTimer);
      Polling.factTimer = setInterval(function () {
        fi = (fi + 1) % COPY.step3.facts.length;
        factEl.setAttribute('data-fade', 'out');
        setTimeout(function () { factEl.textContent = COPY.step3.facts[fi]; factEl.removeAttribute('data-fade'); }, reducedMotion() ? 0 : 350);
      }, 7000);
      Polling.start(checkId, pollMs, {
        onProgress: Page.updateProgress,
        onComplete: function (result) { Page.checkComplete(result, true); },
        onDeferred: function () { Analytics.push('bzs_check_deferred'); Page.showDeferred(); },
        onFailed: function () { Page.showFailed(); }
      });
    },
    checkComplete: function (result, scroll) {
      clearInterval(Polling.factTimer);
      var card = $('[data-s3-card]', Page.root);
      if (!$('.bzs-progress', card)) card.innerHTML = Page.runHtml(Page.s.biz || { name: result.business.name, city: result.business.city });
      Page.updateProgress({ stepsDone: STEP_KEYS.slice(), currentStep: 'scoring', percent: 100 });
      $('.bzs-progress', card).setAttribute('data-done', 'true');
      $('[data-s3-title]', card).textContent = COPY.step3.doneTitle;
      $('[data-s3-sub]', card).textContent = COPY.step3.doneSub;
      var extra = $('[data-s3-extra]', card); if (extra) extra.hidden = true;
      Page.live(COPY.step3.doneTitle);
      Analytics.push('bzs_check_complete', { score: result.score, grade: result.grade });
      setTimeout(function () { Page.showResults(result, scroll); }, scroll ? 650 : 0);
    },
    showResults: function (result, scroll) {
      Page.s.result = result; Page.s.hasResult = true;
      var btn = $('[data-s2-submit]', Page.root); if (btn) btn.textContent = COPY.step2.buttonAgain;
      var box = $('#bzs-results', Page.root);
      box.innerHTML = Results.html(result, { email: Page.s.email });
      box.hidden = false;
      box.classList.remove('bzs-reveal'); void box.offsetWidth; box.classList.add('bzs-reveal');
      Results.animate(box);
      Share.bind(box, result, Page.s.lt);
      Page.track(4);
      if (scroll) { scrollToEl(box); focusEl($('[data-bzs-results-title]', box)); }
    },

    /* ---------- states ---------- */
    alertHtml: function (msg, retry) {
      return '<div class="bzs-alert" role="alert" tabindex="-1">' + icon('alert') + '<div class="bzs-alert__body"><span>' + esc(msg) + '</span>' +
        (retry ? '<button type="button" class="bzs-pill bzs-pill--ghost" data-bzs-retry>' + icon('refresh') + esc(COPY.errors.tryAgain) + '</button>' : '') + '</div></div>';
    },
    summaryHtml: function (b, email) {
      var L = COPY.summaryLabels;
      var rows = [[L.website, b.website], [L.name, b.name], [L.city, b.city], [L.category, b.category]];
      if (email) rows.push([L.email, email]);
      return '<div class="bzs-summary">' + rows.map(function (r) { return Results.stat(r[0], esc(r[1])); }).join('') + '</div>';
    },
    deferredHtml: function (b, email) {
      var S = COPY.states;
      return '<div class="bzs-state">' +
        '<span class="bzs-state__icon bzs-state__icon--warn">' + icon('clock') + '</span>' +
        '<h2 class="bzs-state__title" id="bzs-s3-title" tabindex="-1">' + esc(S.deferredTitle) + '</h2>' +
        '<p class="bzs-state__text">' + (email ? fmt(S.deferredText, { email: email }).replace(esc(email), '<strong>' + esc(email) + '</strong>') : esc(S.deferredTextNoEmail)) + '</p>' +
        '<p class="bzs-order__label">' + esc(S.savedTitle) + '</p>' +
        Page.summaryHtml(b || {}) +
        '<div class="bzs-btnrow"><a class="bzs-pill bzs-pill--ghost bzs-pill--lg" href="' + esc(CFG.homeUrl) + '">' + icon('home') + esc(S.home) + '</a>' +
        '<a class="bzs-pill bzs-pill--primary bzs-pill--lg" href="' + esc(CFG.storeUrl) + '" data-bzs-cta="store|deferred">' + icon('bag') + esc(S.store) + '</a></div>' +
      '</div>';
    },
    limitHtml: function (d) {
      var S = COPY.states, retry = d.retryAfter ? fmtDateTime(d.retryAfter) : '';
      return '<div class="bzs-state">' +
        '<span class="bzs-state__icon bzs-state__icon--blue">' + icon('calendar') + '</span>' +
        '<h2 class="bzs-state__title" id="bzs-s3-title" tabindex="-1">' + esc(S.limitTitle) + '</h2>' +
        '<p class="bzs-state__text">' + fmt(S.limitText, { email: d.maskedEmail || '' }).replace(esc(d.maskedEmail || ''), '<strong>' + esc(d.maskedEmail || '') + '</strong>') +
        (retry ? ' ' + fmt(S.limitRetry, { date: retry }) : '') + '</p>' +
        '<div data-limit-alert></div>' +
        '<div class="bzs-btnrow">' +
          (d.existingCheckId ? '<button type="button" class="bzs-pill bzs-pill--primary bzs-pill--lg" data-bzs-view-today="' + esc(d.existingCheckId) + '">' + icon('eye') + '<span>' + esc(S.viewToday) + '</span></button>' : '') +
          '<a class="bzs-pill bzs-pill--ghost bzs-pill--lg" href="' + esc(productUrl('audit')) + '" data-bzs-cta="audit|limit">' + esc(COPY.cta.button) + '</a>' +
        '</div></div>';
    },
    failedHtml: function () {
      var S = COPY.states;
      return '<div class="bzs-state">' +
        '<span class="bzs-state__icon bzs-state__icon--danger">' + icon('alert') + '</span>' +
        '<h2 class="bzs-state__title" id="bzs-s3-title" tabindex="-1">' + esc(S.errorTitle) + '</h2>' +
        '<p class="bzs-state__text" role="alert">' + esc(S.errorText) + '</p>' +
        '<div class="bzs-btnrow"><button type="button" class="bzs-pill bzs-pill--primary bzs-pill--lg" data-bzs-retry>' + icon('refresh') + esc(COPY.errors.tryAgain) + '</button></div>' +
      '</div>';
    },
    showDeferred: function () {
      clearInterval(Polling.factTimer);
      var card = $('[data-s3-card]', Page.root);
      card.innerHTML = Page.deferredHtml(Page.s.biz, Page.s.email);
      Page.showStep(3, true);
      focusEl($('#bzs-s3-title', card));
    },
    showFailed: function () {
      clearInterval(Polling.factTimer);
      var card = $('[data-s3-card]', Page.root);
      card.innerHTML = Page.failedHtml();
      Page.showStep(3, true);
      $('[data-bzs-retry]', card).addEventListener('click', function () { Page.submitCheck(); });
      focusEl($('#bzs-s3-title', card));
    },
    showLimit: function (d) {
      Polling.stop();
      var card = $('[data-s3-card]', Page.root);
      card.innerHTML = Page.limitHtml(d);
      Page.showStep(3, true);
      focusEl($('#bzs-s3-title', card));
      var v = $('[data-bzs-view-today]', card);
      if (v) v.addEventListener('click', function () {
        Page.busy(v, true, COPY.states.loading);
        Api.getCheck(v.getAttribute('data-bzs-view-today')).then(function (res) {
          Page.busy(v, false, COPY.states.viewToday);
          if (res.ok && res.data.status === 'complete' && res.data.result) {
            Page.s.checkId = res.data.result.checkId;
            Page.checkComplete(res.data.result, true);
          } else if (res.ok && (res.data.status === 'running' || res.data.status === 'queued')) {
            Page.startCheck(v.getAttribute('data-bzs-view-today'), CFG.pollDefaultMs);
          } else {
            $('[data-limit-alert]', card).innerHTML = Page.alertHtml(COPY.errors.server);
          }
        });
      });
    },

    /* ---------- FAQ and final CTA ---------- */
    faqHtml: function () {
      var F = COPY.faq;
      return '<section class="bzs-faq bzs-section-gap" aria-labelledby="bzs-faq-title">' +
        '<div class="bzs-faq__head"><span class="bzs-eyebrow">' + esc(F.eyebrow) + '</span><h2 class="bzs-faq__title" id="bzs-faq-title">' + esc(F.title) + '</h2></div>' +
        '<div class="bzs-faq__list">' + F.items.map(function (it, i) {
          var open = i === 0;
          var ans = esc(it[2]).replace('{privacy}', '<a href="' + esc(CFG.privacyUrl) + '">' + esc(F.privacyWord) + '</a>');
          return '<div class="bzs-faq__item">' +
            '<h3 style="margin:0"><button type="button" class="bzs-faq__q" id="bzs-faq-q' + i + '" aria-expanded="' + open + '" aria-controls="bzs-faq-a' + i + '">' +
              '<span class="bzs-faq__icon">' + icon(it[0]) + '</span><span class="bzs-faq__text">' + esc(it[1]) + '</span>' + icon('chevron', 'bzs-faq__chev') +
            '</button></h3>' +
            '<div class="bzs-faq__a" id="bzs-faq-a' + i + '" role="region" aria-labelledby="bzs-faq-q' + i + '" data-open="' + open + '"' + (open ? '' : ' inert') + '><div><p>' + ans + '</p></div></div>' +
          '</div>';
        }).join('') + '</div></section>';
    },
    bindFaq: function () {
      var qs = $$('.bzs-faq__q', Page.root);
      qs.forEach(function (q) {
        q.addEventListener('click', function () {
          var willOpen = q.getAttribute('aria-expanded') !== 'true';
          qs.forEach(function (o) {
            var a = document.getElementById(o.getAttribute('aria-controls'));
            var isThis = o === q && willOpen;
            o.setAttribute('aria-expanded', String(isThis));
            a.setAttribute('data-open', String(isThis));
            if (isThis) a.removeAttribute('inert'); else a.setAttribute('inert', '');
          });
        });
      });
    },
    finalHtml: function () {
      var F = COPY.final;
      return '<section class="bzs-cta-final bzs-section-gap" aria-labelledby="bzs-final-title">' +
        '<span class="bzs-eyebrow">' + esc(F.eyebrow) + '</span>' +
        '<h2 class="bzs-cta-final__title" id="bzs-final-title">' + esc(F.title) + '</h2>' +
        '<p class="bzs-cta-final__text">' + esc(F.text) + '</p>' +
        '<div class="bzs-btnrow"><a class="bzs-pill bzs-pill--primary bzs-pill--lg" href="' + esc(productUrl('audit')) + '" data-bzs-cta="audit|final_cta">' + esc(F.primary) + icon('arrow') + '</a>' +
        '<a class="bzs-pill bzs-pill--ghost bzs-pill--lg" href="' + esc(CFG.storeUrl) + '" data-bzs-cta="store|final_cta">' + esc(F.secondary) + '</a></div>' +
        '<p class="bzs-trust">' + F.trust.map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') + '</p>' +
      '</section>';
    },

    /* ---------- helpers ---------- */
    fieldError: function (input, msg) {
      if (!input) return;
      var err = document.getElementById(input.getAttribute('aria-describedby'));
      if (msg) {
        input.setAttribute('aria-invalid', 'true');
        if (err) err.innerHTML = icon('alert') + '<span>' + esc(msg) + '</span>';
      } else {
        input.removeAttribute('aria-invalid');
        if (err) err.innerHTML = '';
      }
    },
    busy: function (btn, on, label) {
      if (!btn) return;
      if (on) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; btn.innerHTML = '<span class="bzs-spin" aria-hidden="true"></span><span>' + esc(label) + '</span>'; }
      else { btn.removeAttribute('aria-busy'); btn.disabled = false; btn.textContent = label; }
    },
    live: function (msg) { var l = $('[data-bzs-live]', Page.root); if (l) { l.textContent = ''; setTimeout(function () { l.textContent = msg; }, 60); } },
    /* n: 1..3 = that step active; 4 = all done */
    track: function (n) {
      var I = COPY.intro;
      $$('.bzs-tracker__item', Page.root).forEach(function (li) {
        var i = Number(li.getAttribute('data-track'));
        var st = i < n ? 'done' : i === n ? 'active' : 'pending';
        li.setAttribute('data-state', st);
        if (st === 'active') li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
        var num = $('.bzs-tracker__num', li);
        num.innerHTML = st === 'done' ? icon('check') : String(i);
        if (st === 'done') { var s = $('svg', num); s.style.width = '14px'; s.style.height = '14px'; }
        $('[data-track-sr]', li).textContent = ', ' + (st === 'done' ? I.stepDone : st === 'active' ? I.stepActive : I.stepPending);
      });
      $$('.bzs-tracker__line', Page.root).forEach(function (l) { l.setAttribute('data-done', String(Number(l.getAttribute('data-line')) < n)); });
    },
    showStep: function (n, scroll) {
      var el = $('#bzs-step' + n, Page.root);
      if (!el) return;
      var wasHidden = el.hidden;
      el.hidden = false;
      if (wasHidden) { el.classList.remove('bzs-reveal'); void el.offsetWidth; el.classList.add('bzs-reveal'); }
      if (n === 2 && !el.getAttribute('data-bound')) { el.setAttribute('data-bound', '1'); Page.bindStep2(); }
      Page.track(n);
      if (scroll) {
        setTimeout(function () {
          scrollToEl(el);
          var h = $('h2', el); focusEl(h);
          if (n === 2) { var w = $('#bzs-website', el); if (w && !w.value) try { w.focus({ preventScroll: true }); } catch (e) { w.focus(); } }
        }, 80);
      }
    },

    /* ---------- shared result (/pages/ai-score?r=) ---------- */
    showShared: function (shareId) {
      var box = $('[data-bzs-shared]', Page.root);
      box.hidden = false;
      box.innerHTML = '<div class="bzs-banner"><p class="bzs-banner__text">' + icon('sparkle') + '<span>' + esc(COPY.shared.text) + '<small>' + esc(COPY.shared.small) + '</small></span></p>' +
        '<a class="bzs-pill bzs-pill--primary" href="#bzs-step1" data-bzs-to-step1>' + esc(COPY.shared.button) + icon('arrow') + '</a></div>' +
        '<div data-bzs-shared-results><div class="bzs-card"><p class="bzs-small"><span class="bzs-spin" style="display:inline-block;vertical-align:middle;margin-right:8px"></span>' + esc(COPY.states.loading) + '</p></div></div>';
      $('[data-bzs-to-step1]', box).addEventListener('click', function (e) {
        e.preventDefault();
        scrollToEl($('#bzs-step1', Page.root));
        var em = $('#bzs-email', Page.root); if (em) try { em.focus({ preventScroll: true }); } catch (x) { em.focus(); }
      });
      Api.getResult(shareId).then(function (res) {
        var holder = $('[data-bzs-shared-results]', box);
        if (res.ok && res.data && res.data.score != null) {
          holder.innerHTML = '<div class="bzs-results" style="margin-top:0">' + Results.html(res.data, { shared: true }) + '</div>';
          Results.animate(holder);
          $('.bzs-intro', Page.root).classList.add('bzs-section-gap');
        } else {
          holder.innerHTML = '';
        }
      });
    },

    /* ---------- review view (?bzview=all): every step and state at once ---------- */
    showReview: function () {
      var band = /^(low|mid|high)$/.test(params().get('bzmock') || '') ? params().get('bzmock') : 'low';
      var biz = DEFAULT_BIZ;
      Page.s.review = true;
      Page.s.lt = 'lt_demo'; Page.s.email = 'dan@northlineplumbing.ca';
      Page.renderStep1('done');
      Page.showStep(2, false);
      $('#bzs-website', Page.root).value = biz.website;
      $('#bzs-name', Page.root).value = biz.name;
      $('#bzs-city', Page.root).value = biz.city;
      $('#bzs-category', Page.root).value = biz.category;
      Page.s.biz = { website: biz.website, name: biz.name, city: biz.city, category: biz.category };
      $('#bzs-step3', Page.root).hidden = false;
      var result = buildResult(band, biz, 'ck_review');
      Page.checkComplete(result, false);
      var rv = $('[data-bzs-review]', Page.root);
      rv.hidden = false;
      var card = function (inner) { return '<div class="bzs-card" style="margin-top:20px">' + inner + '</div>'; };
      var tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(9, 0, 0, 0);
      rv.innerHTML = '<div class="bzs-section-gap"><span class="bzs-eyebrow">' + esc(COPY.review.eyebrow) + '</span><h2 class="bzs-h2" style="margin-top:12px">' + esc(COPY.review.title) + '</h2></div>' +
        card(Page.deferredHtml(Page.s.biz, Page.s.email)) +
        card(Page.limitHtml({ maskedEmail: maskEmail(Page.s.email), existingCheckId: 'ck_456', retryAfter: tomorrow.toISOString() })) +
        card(Page.failedHtml()) +
        card(Page.alertHtml(COPY.errors.bot, true)) +
        card(Page.alertHtml(COPY.errors.server, true));
      $$('[id="bzs-s3-title"]', rv).forEach(function (h) { h.removeAttribute('id'); });
    },

    /* ---------- init ---------- */
    init: function (root) {
      Page.root = root;
      utm();
      Page.skeleton();
      Page.bindFaq();
      root.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('[data-bzs-cta]') : null;
        if (!a) return;
        var parts = a.getAttribute('data-bzs-cta').split('|');
        Analytics.push('bzs_cta_click', { product: parts[0], placement: parts[1] || '' });
      });
      var p = params();
      if (p.get('bzview') === 'all') { Page.showReview(); return; }
      if (p.get('r')) Page.showShared(p.get('r'));
      Page.track(1);

      var lt = p.get('lt');
      var pending = store.get('sessionStorage', KEYS.pendingLead);
      if (lt) {
        $('[data-s1-body]', root).innerHTML = '<p class="bzs-small"><span class="bzs-spin" style="display:inline-block;vertical-align:middle;margin-right:8px"></span>' + esc(COPY.states.loading) + '</p>';
        Api.getLead(lt).then(function (res) {
          if (res.ok && res.data && res.data.email) Page.leadDone(lt, res.data.email, false);
          else Page.renderStep1('form');
        });
      } else if (pending && pending.email) {
        /* the pop-up could not reach the API; submit it from here */
        store.del('sessionStorage', KEYS.pendingLead);
        $('[data-s1-body]', root).innerHTML = '<p class="bzs-small"><span class="bzs-spin" style="display:inline-block;vertical-align:middle;margin-right:8px"></span>' + esc(COPY.states.loading) + '</p>';
        Api.createLead(pending.email, 'popup').then(function (res) {
          if (res.ok && res.data.leadToken) {
            Analytics.push('bzs_lead_submitted', { source: 'popup' });
            Page.leadDone(res.data.leadToken, res.data.email || pending.email, false);
          } else {
            Page.renderStep1('form', pending.email, Api.code(res) === 'invalid_email' ? COPY.errors.email : COPY.errors.server);
          }
        });
      } else {
        Page.renderStep1('form');
      }
    }
  };

  /* ===================================================================
     13. POPUP (home page only) + corner badge
     =================================================================== */
  var Popup = {
    el: null, badge: null, shown: false, timers: [],
    snoozed: function () {
      var at = Number(store.get('localStorage', KEYS.popupClosedAt)) || 0;
      return at && (Date.now() - at) < CFG.popup.snoozeDays * 864e5;
    },
    html: function () {
      var P = COPY.popup;
      return '' +
        '<div class="bzs-pop" role="dialog" aria-modal="false" aria-labelledby="bzs-pop-title" data-open="false">' +
          '<button type="button" class="bzs-pop__close" aria-label="' + esc(P.close) + '" data-pop-close>' + icon('x') + '</button>' +
          '<div class="bzs-pop__grid">' +
            '<div>' +
              '<span class="bzs-eyebrow">' + esc(P.eyebrow) + '</span>' +
              '<h2 class="bzs-pop__title" id="bzs-pop-title">' + esc(P.title) + '</h2>' +
              '<p class="bzs-pop__hook">' + esc(P.hook) + '</p>' +
            '</div>' +
            '<form class="bzs-pop__form" novalidate data-pop-form>' +
              '<div class="bzs-field">' +
                '<label class="bzs-label" for="bzs-pop-email">' + esc(P.label) + '<span class="bzs-req" aria-hidden="true">*</span></label>' +
                '<div class="bzs-pop__row">' +
                  '<input class="bzs-input" id="bzs-pop-email" type="email" inputmode="email" autocomplete="email" required placeholder="' + esc(P.placeholder) + '" aria-describedby="bzs-pop-email-err">' +
                  '<button class="bzs-btn" type="submit">' + esc(P.button) + '</button>' +
                '</div>' +
                '<p class="bzs-error" id="bzs-pop-email-err" role="alert"></p>' +
              '</div>' +
              '<div class="bzs-field">' +
                '<label class="bzs-check"><input type="checkbox" id="bzs-pop-consent" required aria-describedby="bzs-pop-consent-err">' +
                '<span>' + esc(COPY.consent.text) + ' <a href="' + esc(CFG.privacyUrl) + '" target="_blank" rel="noopener">' + esc(COPY.consent.privacy) + '</a></span></label>' +
                '<p class="bzs-error" id="bzs-pop-consent-err" role="alert"></p>' +
              '</div>' +
              '<p class="bzs-pop__free">' + icon('shield') + esc(P.free) + '</p>' +
            '</form>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="bzs-badge" hidden data-pop-badge><span class="bzs-badge__dot">' + icon('sparkle') + '</span>' + esc(P.badge) + '</button>';
    },
    open: function (focus) {
      if (!Popup.el) return;
      Popup.timers.forEach(clearTimeout);
      window.removeEventListener('scroll', Popup.onScroll);
      Popup.badge.hidden = true;
      Popup.el.setAttribute('data-open', 'true');
      if (!Popup.shown) { Popup.shown = true; Analytics.push('bzs_popup_shown'); }
      document.addEventListener('keydown', Popup.onKey);
      if (focus) setTimeout(function () { var i = $('#bzs-pop-email', Popup.el); if (i) i.focus(); }, 120);
    },
    close: function () {
      Popup.el.setAttribute('data-open', 'false');
      document.removeEventListener('keydown', Popup.onKey);
      store.set('localStorage', KEYS.popupClosedAt, Date.now());
      Popup.badge.hidden = false;
      Analytics.push('bzs_popup_closed');
      if (Popup.el.contains(document.activeElement)) Popup.badge.focus();
    },
    onKey: function (e) { if (e.key === 'Escape' || e.key === 'Esc') Popup.close(); },
    onScroll: function () {
      var h = document.documentElement, max = (h.scrollHeight - window.innerHeight) || 1;
      if ((window.scrollY || h.scrollTop) / max >= CFG.popup.scrollRatio) Popup.open(false);
    },
    submit: function (form) {
      var email = $('#bzs-pop-email', form), consent = $('#bzs-pop-consent', form), ok = true;
      if (!isEmail(email.value)) { Page.fieldError(email, COPY.errors.email); ok = false; }
      if (!consent.checked) { Page.fieldError(consent, COPY.errors.consent); ok = false; }
      if (!ok) { (isEmail(email.value) ? consent : email).focus(); return; }
      var btn = $('button[type="submit"]', form);
      Page.busy(btn, true, COPY.popup.sending);
      var go = function (lt) {
        var url = CFG.pageUrl + (lt ? (CFG.pageUrl.indexOf('?') > -1 ? '&' : '?') + 'lt=' + encodeURIComponent(lt) : '');
        window.location.href = url;
      };
      Api.createLead(email.value, 'popup').then(function (res) {
        if (res.ok && res.data.leadToken) {
          store.set('localStorage', KEYS.popupDone, 1);
          Analytics.push('bzs_lead_submitted', { source: 'popup' });
          go(res.data.leadToken);
          return;
        }
        var code = Api.code(res);
        Page.busy(btn, false, COPY.popup.button);
        if (code === 'invalid_email') { Page.fieldError(email, COPY.errors.email); email.focus(); return; }
        if (code === 'consent_required') { Page.fieldError(consent, COPY.errors.consent); consent.focus(); return; }
        /* API down: save and let the page submit it */
        store.set('sessionStorage', KEYS.pendingLead, { email: email.value.trim(), consent: { given: true, text: COPY.consent.text + ' ' + COPY.consent.privacy, at: new Date().toISOString(), pageUrl: window.location.href } });
        store.set('localStorage', KEYS.popupDone, 1);
        go(null);
      });
    },
    init: function (mount) {
      if (store.get('localStorage', KEYS.popupDone)) return; /* never again after a successful submit */
      utm();
      mount.innerHTML = Popup.html();
      Popup.el = $('.bzs-pop', mount);
      Popup.badge = $('[data-pop-badge]', mount);
      var form = $('[data-pop-form]', mount);
      $('[data-pop-close]', mount).addEventListener('click', Popup.close);
      Popup.badge.addEventListener('click', function () { Analytics.push('bzs_badge_reopened'); Popup.open(true); });
      $('#bzs-pop-email', form).addEventListener('input', function (e) { Page.fieldError(e.target, ''); });
      $('#bzs-pop-consent', form).addEventListener('change', function (e) { Page.fieldError(e.target, ''); });
      form.addEventListener('submit', function (e) { e.preventDefault(); Popup.submit(form); });
      if (params().get('bzpopup') === 'now') { Popup.open(false); return; }
      if (Popup.snoozed()) { Popup.badge.hidden = false; return; }
      Popup.timers.push(setTimeout(function () { Popup.open(false); }, CFG.popup.delayMs));
      window.addEventListener('scroll', Popup.onScroll, { passive: true });
    }
  };

  /* ===================================================================
     14. INIT
     =================================================================== */
  function boot() {
    var pageRoot = document.querySelector('[data-bzs-page]');
    if (pageRoot) Page.init(pageRoot);
    var popRoot = document.querySelector('[data-bzs-popup]');
    if (popRoot && !pageRoot) Popup.init(popRoot);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Small debug hook for the developer (read-only use). */
  window.BZS = { config: CFG, copy: COPY, fixtures: buildResult, version: '1.0.0' };
})();
